import { useState, useMemo, useRef, useEffect, useCallback } from 'react'
import { tippingFromRows, runsByDeliveryFromRows, loansByDeliveryFromRows, type TippingSummaryRow } from '@/lib/persistShapes'
import { POLL } from '@/lib/pollTiers'
import { RUN_STATUS_CHIPS } from '@/lib/runsRegister'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { updateWithVersion, isStaleWriteError } from '@/lib/optimistic'
import { sastTodayYMD, fmtDateZA } from '@/lib/dates'
import { useNavigate, Link } from 'react-router-dom'
import { supabase } from '@/lib/supabase'
import { withTimeout, withReadTimeout } from '@/lib/saveTimeout'
import { useSaveDb, ensureSaveDb } from '@/lib/useSaveDb'
import { intakePathLabel, intakePathForLine, fruitList } from '@/lib/intakePath'
import { useLineDrench } from '@/hooks/useCommodities'
import { findTodaysDelivery } from '@/lib/todaysDelivery'
import { ValidationError, isUniqueViolation } from '@/lib/errorReporter'
import { useCurrentUser } from '@/lib/useCurrentUser'
import { usePermissions } from '@/lib/usePermissions'
import { usePersistedState } from '@/hooks/usePersistedState'
import { useTenantPersistedState } from '@/hooks/useTenantPersistedState'
import { isAdmin, isManagerOrAbove, canAddGrowerFromIntake, tenantRoleOf } from '@/lib/roles'
import { CONTAINER_LABEL, type ContainerType } from '@/lib/containerTypes'
import ContainerTypePicker, { decodeContainerPickerValue, encodeEnum, type ContainerPickerValue } from '@/components/ContainerTypePicker'
import TeachingEmptyState from '@/components/TeachingEmptyState'
import { useFlowManifest } from '@/hooks/useCommodityTemplates'
import SubmitOverlay from '@/components/SubmitOverlay'
import QueryStateGate from '@/components/QueryStateGate'
import FilterFold from '@/components/FilterFold'
import { growerBinTypeFollowingFruit } from './bin-inventory/tabs/grower-bins/growerBinHoldings'
import type { Delivery, DeliveryAddition, DeliveryStatus, IntakePath } from '@/types/database'


import { compressStandard, safeCompress, uploadWithTimeout, tenantPath } from '@/lib/storage'
import { useActiveTenant } from '@/lib/useActiveTenant'
import { capabilityOn } from '@/lib/capabilityCatalog'
import { parseKg, crateTareKg, intakeNet, intakeWeightProblem } from '@/lib/intakeWeight'
import { palletLineProblem, palletLineSums, palletPayload, type PalletDraft } from '@/lib/deliveryPallets'
import IntakePalletsField from './IntakePalletsField'
import { deliveryHasPallets } from '@/lib/deliveryPalletsDb'
import { useQcPickableTemplates } from '@/hooks/useQcPickableTemplates'
import { templatesForDelivery } from './DeliveryQcStart'
import { insertOrQueueDelivery } from '@/lib/offlineQueue'
import { useDuplicateNote, type DuplicateNoteRow } from '@/hooks/useDuplicateNote'
import { DuplicateNoteAdvisory, duplicateNoteSentence } from '@/components/DuplicateNoteAdvisory'
import { initialStatusForPath } from '@/lib/deliveryStatus'
import { RunsForDeliverySheet } from './RunsForDeliverySheet'
import { DeliveryActionButtons } from './DeliveryActionButtons'
import { useDeliveryTransition, logStatusChange } from './useDeliveryTransition'
import { useDraftNotComing, mayMarkNotComing } from './useDraftNotComing'
import { Plus, MessageCircle, Truck, Camera, AlertTriangle, ChevronRight, FileText, Package, PackagePlus, X, ShieldCheck, Printer } from 'lucide-react'
import FieldScopedSearch from '@/components/FieldScopedSearch'
import { DeliveriesNoMatch } from './DeliveriesNoMatch'
import { useHistoryWindow } from '@/hooks/useHistoryWindow'
import PageHeader from '@/components/PageHeader'
import { Button, StatusPill } from '@/components/ui'
import { statusTone } from '@/lib/statusTone'
import MoreMenu from '@/components/MoreMenu'
import { usePageDirtyGuard } from '@/hooks/usePageDirtyGuard'
import DeliveryPointPicker from '@/components/DeliveryPointPicker'
import { filterDeliveries, needsCheck, boardSummary, fetchAllDeliveries, FILTER_STORAGE_KEYS, UNSPECIFIED_DELIVERY_POINT, type SearchField } from '@/lib/deliveriesFilter'
import { splitAnnouncementsByAge } from '@/lib/announcementFold'
import { readRunAllocationRows, readEmptiesLoanRows, readTippingSummaryRows } from '@/lib/deliveryBoardReads'
import { PalletViewToggle, type PalletView } from '@/components/PalletTable'
import DeliveriesTable from '@/components/DeliveriesTable'
import { PutOnRunButton } from './PutOnRunButton'
import { toDeliveryRow } from '@/lib/deliveryColumns'
import { useBinTypes } from '@/hooks/useBinTypes'
import clsx from 'clsx'
import ComboField from '@/components/ComboField'
import VarietyFromBlockField from '@/components/VarietyFromBlockField'
import { useIntakeLookups } from '@/hooks/useLookupValues'
import { useVehicleRegs } from '@/hooks/useVehicleRegs'
import { useDriverNames, useGrowerLastTruck } from '@/hooks/useTruckHistory'
import { SearchableSelect } from '@/components/SearchableSelect'
import { pucAutoFill, lastTruckOffer } from '@/lib/gateIntakePrefill'
import { useGrowers, usePucs, useBlocks } from '@/hooks/useGrowerData'
import { useDeliveryPoints } from '@/lib/usePartyOptions'
import { alertDialog, choiceDialog, promptDialog } from '@/components/Dialogs'
import PendingFarmLoads from '@/modules/shared/PendingFarmLoads'
import { newUuid } from '@/lib/uuid'
import { friendlyError } from '@/lib/friendlyError'
import { DELIVERY_SCREEN_DOMAIN } from '@/lib/packhouseErrorDomains'
import { patchDeliveryAtCurrentVersion, withPhotoLossNote } from '@/lib/gateIntakeFollowUp'
import type { SupabaseClient } from '@supabase/supabase-js'
import { useWhatsAppStatus } from '@/hooks/useWhatsAppStatus'
import { WhatsAppPausedNote } from '@/components/WhatsAppPausedNote'
import { notifyGrower, type NotifyResult } from '@/lib/whatsappStatus'
import SavedBanner, { SavedBannerLink } from '@/components/SavedBanner'
import { savedIntakeBanner, addedLoadNumber, type SavedIntake, type SavedIntakeLine } from '@/lib/gateSavedSummary'
import { useReturnHereState } from '@/lib/returnTo'
import { countOlderPendingPaltrack, paltrackWindowState } from '@/lib/nextPendingPaltrack'
import { useRegisterScroll, registerRow } from '@/hooks/useRegisterScroll'

// ---------------------------------------------------------------------------
// Status display config
// ---------------------------------------------------------------------------


// The delivery type as the buttons word it, never the raw enum.
const DELIVERY_TYPE_LABEL = { EXPORT: 'Export', LOCAL: 'Local', IMPORT: 'Import' } as const
// A linked run's status in the register's own words (Active, Tipping stopped…).
const runLinkStatusLabel = (status: string) => RUN_STATUS_CHIPS.find(c => c.value === status)?.label ?? status

// Operator-facing list, in display order.
const INTAKE_PATH_OPTIONS: { value: IntakePath; label: string; help: string }[] = [
  { value: 'NOT_DRENCHED', label: 'OG (Orchard Grade)', help: '→ Goes to Awaiting Drench. Drench here, then choose In Stock or Colour-Up.' },
  { value: 'DRENCHED',     label: 'Already Drenched',   help: '→ Goes to Drenched. Next, choose In Stock or Colour-Up.' },
  { value: 'REBINNED',     label: 'Rebinned',           help: '→ Goes straight to In Stock.' },
]

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------
type View = 'board' | 'intake' | 'confirm-draft'

// One fruit line off the truck: a variety and/or bin type. The main form
// fields are line 1; extra_lines carry the rest of a mixed load. Each line
// saves as its OWN delivery (auto-grouped independently) so tipping tare,
// run allocation and reporting stay per-variety, per-container.
interface IntakeLine {
  block_id: string
  block: string
  variety: string
  bins: string
  fruit_container_type: ContainerPickerValue
  // Pallets off the truck (spec #4006), where the business weighs at intake:
  // bins is then their crates' sum. pallet_tare: kg, each pallet of the line.
  pallets: PalletDraft[]
  pallet_tare: string
}

interface IntakeForm {
  grower_id: string
  grower: string
  puc_id: string
  puc: string
  block_id: string
  block: string
  variety: string
  bins_received: string
  // Optional: the container the FRUIT arrived in (mig 652 + fruit_bin_type_id).
  // Encoded ContainerPickerValue: '' = not declared (DB keeps legacy tare
  // resolution), 'enum:<TYPE>' = a fixed enum, 'type:<uuid>' = a custom grower
  // bin type. Decoded at save into fruit_container_type + fruit_bin_type_id.
  fruit_container_type: ContainerPickerValue
  // Intake weight + QC (spec #3956, mig 20261185000000), asked only where the
  // business has the intake_weight switch on (J1: Fresca). Typed text, parsed
  // at save; the weight is line 1's. qc_template_id: a QC template to run on
  // the delivery straight after save ('' = none).
  // With the switch on, a manual intake weighs PALLETS (spec #4006): line 1's
  // are `pallets`, and other_tare_kg is then the tare of each of its pallets.
  gross_weight_kg: string
  other_tare_kg:   string
  pallets:         PalletDraft[]
  qc_template_id:  string
  delivery_type: 'EXPORT' | 'IMPORT' | 'LOCAL'
  intake_path: IntakePath
  vehicle_reg: string
  driver_name: string
  notes: string
  uses_kf49_bins: boolean
  delivery_note_number: string
  // Admin-only back-date override. Empty string = use server NOW().
  received_at_override: string
  // Issue empties on the same truck. null = operator hasn't picked yet
  // (form blocks submit until they do); false = no, true = yes + sub-fields.
  issue_empties: boolean | null
  empties_bin_count: string
  // Encoded ContainerPickerValue ('enum:<TYPE>' | 'type:<uuid>'); decoded at
  // save into bin_loans.container_type + bin_type_id.
  empties_container_type: ContainerPickerValue
  empties_delivery_note_number: string
  // Track bins the grower brought in their own bins. Only relevant when
  // uses_kf49_bins is false. Defaults to bins_received when toggled on.
  track_grower_bins: boolean
  grower_bin_count: string
  // Encoded ContainerPickerValue ('enum:<TYPE>' | 'type:<uuid>'); decoded at
  // save into grower_bin_holdings.container_type + bin_type_id.
  grower_bin_container_type: ContainerPickerValue
  // True once the operator picks the Bin type; then a later change of "Fruit
  // arrived in" no longer overwrites it (value-matching cannot tell a pick
  // that equals the default from an untouched field).
  grower_bin_type_picked: boolean
  // Additional varieties / bin types on the same truck (manual intake only;
  // WhatsApp drafts stay single-line). Truck-level fields above are shared.
  extra_lines: IntakeLine[]
}

const EMPTY_FORM: IntakeForm = {
  grower_id: '', grower: '',
  puc_id: '',    puc: '',
  block_id: '',  block: '',
  variety: '', bins_received: '',
  fruit_container_type: '',
  gross_weight_kg: '', other_tare_kg: '', pallets: [], qc_template_id: '',
  delivery_note_number: '',
  delivery_type: 'EXPORT',
  intake_path: 'NOT_DRENCHED',
  vehicle_reg: '', driver_name: '', notes: '',
  uses_kf49_bins: true,
  received_at_override: '',
  issue_empties:                false,  // R2 (2026-07-06): default No (the common case); operator taps Yes to issue empties. No longer blocks the fruit record.
  empties_bin_count:            '',
  empties_container_type:       encodeEnum('KF49_BIN'),
  empties_delivery_note_number: '',
  track_grower_bins:            false,
  grower_bin_count:             '',
  grower_bin_container_type:    encodeEnum('RED_ARM_LUG'),
  grower_bin_type_picked:       false,
  extra_lines:                  [],
}

function withFruitContainer(f: IntakeForm, v: ContainerPickerValue): IntakeForm {
  return {
    ...f,
    fruit_container_type: v,
    grower_bin_container_type: growerBinTypeFollowingFruit({
      growerBinType: f.grower_bin_container_type,
      fruitBefore:   f.fruit_container_type,
      fruitAfter:    v,
      defaultType:   EMPTY_FORM.grower_bin_container_type,
      picked:        f.grower_bin_type_picked,
    }),
  }
}

/** A picked file's identity for the dirty snapshot. The File object itself is
 *  a new reference on every pick and stringifies to "{}", so neither works. */
function fileSig(f: File | null): string {
  return f ? `${f.name}:${f.size}` : ''
}

const EMPTY_LINE: IntakeLine = {
  block_id: '', block: '', variety: '', bins: '', fruit_container_type: '',
  pallets: [], pallet_tare: '',
}

/** One fruit line, checked and shaped for save. `weigh` (the intake_weight
 *  switch is on) means every crate is on a pallet and bins is their sum (spec
 *  #4006): the line then carries its pallets and the delivery's weight, their
 *  sums (the server keeps it so). */
function parseIntakeLine(raw: IntakeLine, label: string, weigh: { crateTareKg: number | null } | null) {
  if (!raw.variety.trim()) throw new ValidationError(`${label}Variety is required`)
  const palletIssue = weigh ? palletLineProblem(raw.pallets, raw.pallet_tare, weigh.crateTareKg, label) : null
  if (palletIssue) throw new ValidationError(palletIssue)
  const binsNum = parseInt(raw.bins)
  if (!(binsNum >= 1)) throw new ValidationError(`${label}Enter a valid bin quantity`)
  if (!weigh) return { ...raw, binsNum, pallets: null, lineWeight: null }
  const sums = palletLineSums(raw.pallets, parseKg(raw.pallet_tare), weigh.crateTareKg)
  return {
    ...raw, binsNum,
    pallets: palletPayload(raw.pallets, raw.pallet_tare),
    lineWeight: { gross_weight_kg: sums.grossKg, other_tare_kg: sums.palletTareKg },
  }
}

// Total bins across every line — used for the auto-return hint and the
// grower-bins-held default.
function totalBinsOf(f: IntakeForm): number {
  return [f.bins_received, ...f.extra_lines.map(l => l.bins)]
    .reduce((sum, v) => sum + (parseInt(v) || 0), 0)
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

// Decode the "Fruit arrived in" picker into the two delivery columns. A custom
// bin type wins and NULLs the enum, so resolve_bin_tare_kg falls back cleanly
// through the grower/system ladder if the type is later deactivated. '' = not
// declared → both NULL (legacy tare resolution).
function fruitContainerCols(v: ContainerPickerValue): {
  fruit_container_type: ContainerType | null
  fruit_bin_type_id:    string | null
} {
  if (!v) return { fruit_container_type: null, fruit_bin_type_id: null }
  const { container_type, bin_type_id } = decodeContainerPickerValue(v, [])
  return bin_type_id
    ? { fruit_container_type: null,           fruit_bin_type_id: bin_type_id }
    : { fruit_container_type: container_type, fruit_bin_type_id: null }
}

// ---------------------------------------------------------------------------
// Gate intake: photos, empties and grower bins — shared by Gate Intake and
// Confirm Arrival, so both record what the form shows (gate-intake data-loss
// fix, 2026-09-30: Confirm Arrival showed these fields and wrote none of them).
// ---------------------------------------------------------------------------

/** Compress + upload one gate photo. Never throws: a photo that doesn't upload
 *  is reported to the operator by the caller and the save carries on without
 *  it. Three of the four save paths used to drop a failed photo without a word,
 *  and a compress that threw mid-save aborted a load whose bins had already
 *  been added. */
async function uploadGatePhoto(
  db: SupabaseClient, bucket: string, path: string, file: File,
  compress: (f: Blob) => Promise<Blob> = compressStandard,
): Promise<{ path: string | null; error: string | null }> {
  try {
    const blob = await compress(file)
    const { error } = await uploadWithTimeout(bucket, path, blob,
      { upsert: true, contentType: 'image/jpeg' }, undefined, db)
    return error ? { path: null, error: error.message } : { path, error: null }
  } catch (e) {
    return { path: null, error: e instanceof Error ? e.message : String(e) }
  }
}

/** "Track grower bins held?" is only on screen while "Grower's own bins" is
 *  picked. A Yes left behind by switching back to Packhouse bins is hidden, so
 *  it records nothing. */
function tracksGrowerBins(form: IntakeForm): boolean {
  return form.track_grower_bins && !form.uses_kf49_bins
}

/** The empties / grower-bin answers, checked BEFORE anything is written. They
 *  used to be checked after the delivery was committed: the clerk read the red
 *  line as "not saved", fixed it, tapped Record again, and was offered to add
 *  the same bins onto the delivery a second time. */
function checkTruckExtras(form: IntakeForm, growerId: string | null): void {
  if (form.issue_empties) checkEmptiesAnswers(form, growerId)
  if (tracksGrowerBins(form)) checkGrowerBinAnswers(form, growerId)
}
function checkEmptiesAnswers(form: IntakeForm, growerId: string | null): void {
  if (!growerId) throw new ValidationError('Pick the grower from the dropdown to issue empties (free-typed names are not linked).')
  if (!(parseInt(form.empties_bin_count) > 0)) throw new ValidationError('Enter a bin count for the empties')
  if (!form.empties_delivery_note_number.trim()) throw new ValidationError('Delivery note number is required for the empties')
}
function checkGrowerBinAnswers(form: IntakeForm, growerId: string | null): void {
  if (!growerId) throw new ValidationError('Pick the grower from the dropdown to track grower bins.')
  if (!(parseInt(form.grower_bin_count) > 0)) throw new ValidationError('Enter a bin count for the grower bins held')
}

interface TruckExtras {
  db:               SupabaseClient
  tenantId:         string | null
  userId:           string
  form:             IntakeForm
  growerId:         string
  growerName:       string
  emptiesNotePhoto: File | null
  /** The delivery the empties snapshot and the holding belong to (null when
   *  it went to the offline queue — never, today, with extras on). */
  deliveryId:       string | null
  /** The load the loan is attributed to (mig 342). */
  additionId:       string | null
  /** When: the back-date, else now. */
  atIso:            string
  vehicle:          string | null
  driver:           string | null
}

/** Record the empties loan and the grower-bin holding once the delivery is
 *  written. Never throws: the delivery IS saved by now, so a failure here comes
 *  back as an operator sentence and the save still succeeds. The form then
 *  resets, so a retry cannot add the load a second time, and the alert says
 *  exactly what did and didn't record. */
async function recordTruckExtras(
  x: Omit<TruckExtras, 'atIso' | 'vehicle' | 'driver'> & { atIso: string | null },
): Promise<string[]> {
  const full: TruckExtras = {
    ...x,
    atIso:   x.atIso ?? new Date().toISOString(),
    vehicle: x.form.vehicle_reg.trim() || null,
    driver:  x.form.driver_name.trim() || null,
  }
  const problems: string[] = []
  if (x.form.issue_empties) problems.push(...await recordEmptiesLoan(full))
  if (tracksGrowerBins(x.form)) problems.push(...await recordGrowerBinHolding(full))
  return problems
}

// ── Issue empties on the same truck ──
// The truck that delivered fruit takes empty bins back to the same grower.
// R4 (2026-07-06): the signed-note PHOTO is optional for everyone; the note
// NUMBER (checked up front) is the traceability key. A photo that fails to
// upload no longer throws the whole loan away — the loan is the money.
async function recordEmptiesLoan(x: TruckExtras): Promise<string[]> {
  const problems: string[] = []
  const eb    = parseInt(x.form.empties_bin_count)
  const eNote = x.form.empties_delivery_note_number.trim()
  let emptiesNotePath: string | null = null
  if (x.emptiesNotePhoto) {
    const up = await uploadGatePhoto(x.db, 'dispatch-evidence',
      tenantPath(x.tenantId, `bin-loans/${x.growerId}/${Date.now()}-deliverynote.jpg`),
      x.emptiesNotePhoto, safeCompress)
    emptiesNotePath = up.path
    if (up.error) problems.push(`The signed empties note photo did not upload. ${reasonText(up.error)} The ${eb} empties ARE recorded, without the photo — keep the paper note.`)
  }
  // Decode the bin-type picker → enum stand-in + custom bin_types FK.
  const emptiesType = decodeContainerPickerValue(x.form.empties_container_type, [])
  try {
    const { data: loanRow, error: loanErr } = await withTimeout(x.db.from('bin_loans').insert({
      grower_id:         x.growerId,
      grower_name:       x.growerName,
      bin_count:         eb,
      container_type:    emptiesType.container_type,
      bin_type_id:       emptiesType.bin_type_id,
      issued_at:         x.atIso,
      issued_by:         x.userId,
      vehicle_reg:       x.vehicle,
      driver_name:       x.driver,
      notes:             `Issued at gate alongside delivery — note ${eNote}`,
      truck_photo_url:   null,
      delivery_note_url: emptiesNotePath,
      // Mig 342: link this loan to the specific load it was issued
      // alongside so a grouped delivery can list every empties-out
      // event attributed to the right load.
      delivery_addition_id: x.additionId,
    }).select('loan_id').single(), 'Deliveries: bin_loans')
    if (loanErr) throw loanErr
    const loanId = (loanRow as { loan_id?: string } | null)?.loan_id
    if (loanId && x.deliveryId) {
      await snapshotEmpties(x.db, x.deliveryId, {
        empties_issued_count:         eb,
        // Display snapshot only — bin_loans.bin_type_id is authoritative.
        empties_container_type:       emptiesType.container_type,
        empties_delivery_note_number: eNote,
        empties_loan_id:              loanId,
      })
    }
  } catch (e) {
    problems.push(`The ${eb} empty bins going out were NOT confirmed as recorded. ${reasonText(e)} Before issuing them again, check Empties Out on the delivery and Bin Inventory → Outstanding. If they are missing, an admin issues them from Empties Out — or, when Empties Out already lists another load's empties, from Bin Inventory → Issue.`)
  }
  return problems
}

// Snapshot empties on the parent row so older readers can show "→ N empties
// out" without joining bin_loans. ONLY when the parent has none yet (a later
// load must not clobber the first-load snapshot). Display only: bin_loans is
// authoritative and is what the board and the delivery page read, so a
// failure here loses nothing and is not the operator's problem.
// On the save client and bounded: it runs after the delivery and the loan are
// written, so an unbounded read on the shared client (a getSession wedge)
// would hold a save that has already landed until the 60s watchdog says it
// "may not have gone through".
async function snapshotEmpties(db: SupabaseClient, deliveryId: string, snapshot: Record<string, unknown>): Promise<void> {
  try {
    const { data: snap, error: snapErr } = await withReadTimeout(db
      .from('deliveries')
      .select('empties_loan_id, version')
      .eq('delivery_id', deliveryId)
      .maybeSingle(), 'Deliveries: empties snapshot read')
    if (snapErr) throw snapErr
    const row = snap as { empties_loan_id?: string | null; version?: number } | null
    if (row?.empties_loan_id || row?.version == null) return
    await withTimeout(updateWithVersion('deliveries', { col: 'delivery_id', value: deliveryId }, row.version,
      { ...snapshot, update_reason: 'gate intake — first-load empties snapshot' },
      { db, select: 'delivery_id' }), 'Deliveries: empties snapshot')
  } catch (e) {
    console.warn('Empties snapshot not written; the bin_loans row is authoritative.', e)
  }
}

// ── Track grower-owned bins held at the packhouse ──
// When the grower brings their own bins, log how many and what type are
// sitting at our yard until they fetch them.
async function recordGrowerBinHolding(x: TruckExtras): Promise<string[]> {
  const gb = parseInt(x.form.grower_bin_count)
  // Decode the bin-type picker → enum stand-in + custom bin_types FK.
  const growerBinType = decodeContainerPickerValue(x.form.grower_bin_container_type, [])
  try {
    const { error: holdErr } = await withTimeout(x.db.from('grower_bin_holdings').insert({
      grower_id:      x.growerId,
      grower_name:    x.growerName,
      container_type: growerBinType.container_type,
      bin_type_id:    growerBinType.bin_type_id,
      bin_count:      gb,
      delivery_id:    x.deliveryId,
      received_at:    x.atIso,
      received_by:    x.userId,
      vehicle_reg:    x.vehicle,
      driver_name:    x.driver,
      notes:          'Logged at gate alongside delivery',
    }), 'Deliveries: grower_bin_holdings')
    if (holdErr) throw holdErr
    return []
  } catch (e) {
    return [`The ${gb} grower bins held were NOT confirmed as recorded. ${reasonText(e)} Check Bin Inventory → Grower bins and log them there with Log received bins if they are missing.`]
  }
}

/** What the operator is told when a gate photo didn't make it onto the
 *  record. Only what is true: nothing in the app can add a note photo to a
 *  saved delivery (the old alert sent people to the detail page to
 *  re-photograph it, and that page has no such control). */
function notePhotoProblem(reason: unknown): string {
  return `The delivery-note photo did not upload. ${reasonText(reason)} The delivery is saved and its notes say a photo was taken. Keep the paper note for the office — the photo cannot be added to the delivery afterwards.`
}
function loadPhotoProblem(reason: unknown): string {
  return `The load photo was not saved with the delivery. ${reasonText(reason)} The delivery itself is saved.`
}

/** Why a follow-up write failed, as an operator sentence. A storage or
 *  PostgREST refusal arrives as raw driver text ("new row violates
 *  row-level security policy"), which must never reach the gate. */
function reasonText(reason: unknown): string {
  const m = friendlyError(typeof reason === 'string' ? new Error(reason) : reason).message.trim()
  return /[.!?]$/.test(m) ? m : `${m}.`
}

// Search-field labels for the dropdown in the UI. Values + filter
// semantics live in src/lib/deliveriesFilter.ts (shared with the print
// report).
const SEARCH_FIELDS: { value: SearchField; label: string }[] = [
  { value: 'any',                  label: 'Any field' },
  { value: 'grower',               label: 'Grower' },
  { value: 'variety',              label: 'Variety' },
  { value: 'puc',                  label: 'PUC' },
  { value: 'block',                label: 'Block' },
  { value: 'delivery_note_number', label: 'Delivery note #' },
  { value: 'client_ref_no',        label: 'Client ref' },
  { value: 'vehicle_reg',          label: 'Vehicle reg' },
  { value: 'driver_name',          label: 'Driver' },
]

// ---------------------------------------------------------------------------
// Main page
// ---------------------------------------------------------------------------
type RunLink = { run_id: string; run_number: string; status: string }
type RunDelRow = { delivery_id: string; runs: RunLink | RunLink[] | null }
// Module level so the run-allocations query hands the page the SAME Map until
// its rows change; an inline select would build a new one every render.
const selectRunsByDelivery = (rows: RunDelRow[]) => runsByDeliveryFromRows(rows)

export default function DeliveriesPage() {
  const navigate = useNavigate()
  const qc       = useQueryClient()
  // WhatsApp is paused until the new number is live (Jason, 5 Oct 2026). The
  // gate confirmation says so before Confirm, and the board says what became
  // of the grower's WhatsApp afterwards — never a send that did not happen.
  const wa = useWhatsAppStatus()
  const [waNotice, setWaNotice] = useState<NotifyResult | null>(null)
  // What the last Record Delivery saved (UF-06) — named on the board with
  // Open / Put on a run until dismissed or the next capture starts.
  const [justSaved, setJustSaved] = useState<SavedIntake | null>(null)
  const returnHere = useReturnHereState()
  const { data: me } = useCurrentUser()
  const isAdminUser = isAdmin(me?.role)
  const { data: activeTenant } = useActiveTenant()
  const tenantId = activeTenant?.tenantId ?? null
  // Isolated client for multi-step gate-intake save (deliveries +
  // delivery_additions + optional photo + status_history + maybe
  // bin_loans). Skips navigator.locks contention with the Deliveries
  // page's many useQuery hooks. See src/lib/useSaveDb.ts.
  const saveDb = useSaveDb()
  // Same isolated client, reused for this board's READ burst (deliveries list +
  // tipped-kg + run-allocations + empties-loans + the 30s pollers): on the shared
  // client each query awaits auth.getSession() to resolve its bearer, so on a
  // visibilitychange refresh the whole burst queues behind auth-js's in-process
  // lock — the getSession-wedge that self-heals via reload (getSessionHeal.ts /
  // project_pr173). The isolated client bakes the bearer in and never hits that
  // lock. Falls back to the shared client until the isolated JWT is ready.
  const db = saveDb ?? supabase
  // UF-15: a manager clears an announcement whose truck is not coming.
  const notComing = useDraftNotComing(db)
  const canMarkNotComing = mayMarkNotComing(me)
  // Non-admin operators with this flag (e.g. the gate clerk capturing
  // paper-record back-fills) can also set received_at on a new delivery.
  const { can: canPerm } = usePermissions()
  const canBackDate = isAdminUser || !!me?.can_override_delivery_date || canPerm('pallets', 'override')
  const todaySast = sastTodayYMD()
  const [view,        setView]        = useState<View>('board')
  // Cards ↔ Table layout for the board, persisted like the Pallets view.
  const [boardLayout, setBoardLayout] = usePersistedState<PalletView>('kf49.deliveries.view', 'cards')
  // Filters persist across navigation (going into a delivery and coming back
  // used to wipe the filter, which was annoying in long lists). Clear them
  // explicitly via the "Clear filters" button below the grower/date controls.
  const [storedFilter, setFilter]     = usePersistedState<string>(FILTER_STORAGE_KEYS.statusFilter, 'ALL')
  // UF-07: only people who can tick a check see the chip (same gate as the
  // admin_toggle_addition_checked RPC). A stored NEEDS_CHECK for anyone else reads as All.
  const canSignOff = canPerm('deliveries', 'manage')
  const activeFilter = storedFilter === 'NEEDS_CHECK' && !canSignOff ? 'ALL' : storedFilter
  const [growerFilter,  setGrowerFilter]  = usePersistedState<string>(FILTER_STORAGE_KEYS.growerFilter, '')
  const [varietyFilter, setVarietyFilter] = usePersistedState<string>(FILTER_STORAGE_KEYS.varietyFilter, '')
  const [blockFilter,   setBlockFilter]   = usePersistedState<string>(FILTER_STORAGE_KEYS.blockFilter, '')
  // Point NAMES are tenant-specific, so this one is tenant-scoped — a plain
  // key would carry tenant A's point into tenant B and silently empty the board.
  const [deliveryPointFilter, setDeliveryPointFilter] = useTenantPersistedState<string>(FILTER_STORAGE_KEYS.deliveryPointFilter, '')
  const [fromFilter,    setFromFilter]    = usePersistedState<string>(FILTER_STORAGE_KEYS.fromFilter, '')
  const [toFilter,      setToFilter]      = usePersistedState<string>(FILTER_STORAGE_KEYS.toFilter, '')
  const [searchField,   setSearchField]   = usePersistedState<SearchField>(FILTER_STORAGE_KEYS.searchField, 'any')
  const [searchQuery,   setSearchQuery]   = usePersistedState<string>(FILTER_STORAGE_KEYS.searchQuery, '')
  const [showAllHistory, setShowAllHistory] = useState(false)
  const [form,        setForm]        = useState<IntakeForm>(EMPTY_FORM)
  // Optional delivery-note photo captured at the gate. Uploaded to the
  // existing delivery-photos bucket after the row is created so the upload
  // path can include the new delivery_id.
  const [deliveryNotePhoto, setDeliveryNotePhoto] = useState<File | null>(null)
  // Photo of the signed delivery note for the empties going OUT on the
  // same truck. Optional for admins, required for operators when
  // issue_empties is Yes.
  const [emptiesNotePhoto,  setEmptiesNotePhoto]  = useState<File | null>(null)
  const [draftTarget, setDraftTarget] = useState<Delivery | null>(null)
  const [error,       setError]       = useState<string | null>(null)
  const [photoFile,   setPhotoFile]   = useState<File | null>(null)
  const [photoPreview, setPhotoPreview] = useState<string | null>(null)
  const [actualBins,  setActualBins]  = useState<string>('')
  const [phytcleanOverride, setPhytcleanOverride] = useState(false)
  // Phytclean is the SA citrus export-whitelist scheme; it doesn't apply to a
  // blueberry packhouse, so the intake gate + warning are suppressed for it.
  const usesPhytclean = useFlowManifest().usesPhytclean
  const cameraRef = useRef<HTMLInputElement>(null)

  // Unsaved-work guard for the capture views. Gate Intake is the app's most
  // expensive form to lose: grower, PUC, block, variety, bin counts, extra
  // lines, empties and up to three photos, all typed at the weighbridge with a
  // truck waiting. Before this the back arrow and the bottom nav both threw it
  // away without a word.
  //
  // `null` while the board is showing, so the guard is armed ONLY on a capture
  // view and an operator browsing the register is never asked anything. The
  // baseline arms on the first render of a capture view — which, because both
  // entry points set the form and the view in one handler, is the SEEDED form.
  // That matters for Confirm Arrival: the grower/PUC/block/variety it pre-fills
  // from the WhatsApp announcement are not the operator's typing, so they must
  // not count as unsaved work. The prompt appears once they change something.
  //
  // Files carry name+size rather than the File itself — identity changes on
  // every re-pick, and JSON.stringify(File) is "{}".
  const captureSnapshot = view === 'board' ? null : JSON.stringify({
    form,
    notePhoto:    fileSig(deliveryNotePhoto),
    emptiesPhoto: fileSig(emptiesNotePhoto),
    // Confirm Arrival's own fields — the physical bin count and the load photo.
    actualBins,
    arrivalPhoto: fileSig(photoFile),
    phytcleanOverride,
  })
  usePageDirtyGuard(captureSnapshot)

  // Fetch all deliveries
  const { varieties: varietyLookup } = useIntakeLookups()
  // Variety-from-block override flags (spec deliveries-corrections §2.6):
  // the first line, then one per extra line. Never persisted.
  const [varietyOverride, setVarietyOverride] = useState(false)
  const [lineOverride, setLineOverride] = useState<Record<number, boolean>>({})
  const vehicleRegs = useVehicleRegs()
  const driverNames = useDriverNames()
  const { data: growerList = [] }  = useGrowers()
  // UF-15: the scale operator adds a grower by name from the picker. Same set as the growers INSERT policy.
  const canAddGrower = canAddGrowerFromIntake(tenantRoleOf(me))
  const { data: activeTenantForGrower } = useActiveTenant()
  const addGrowerFromForm = async () => {
    const name = await promptDialog({
      title: 'Add grower',
      placeholder: 'Grower name',
      hint: 'Creates the grower with a name only. A manager adds the PUC and blocks under Growers.',
    })
    const clean = name?.trim()
    if (!clean) return
    const tenantId = activeTenantForGrower?.tenantId
    if (!tenantId) { await alertDialog({ title: 'Grower not added', message: 'No active tenant — refresh and try again.' }); return }
    try {
      const { data, error } = await withTimeout(
        db.from('growers').insert({ name: clean, tenant_id: tenantId }).select('grower_id').single(),
        'Deliveries: grower insert',
      )
      if (error) {
        if (isUniqueViolation(error, 'growers_name_per_tenant_uniq')) throw new ValidationError(`A grower named "${clean}" already exists.`)
        throw error
      }
      const id = (data as { grower_id: string }).grower_id
      await qc.invalidateQueries({ queryKey: ['growers'] })
      setForm(f => ({ ...f, grower_id: id, grower: clean, puc_id: '', puc: '', block_id: '', block: '', variety: '', extra_lines: [] }))
      setPucArmedFor(id)
    } catch (e) {
      await alertDialog({ title: 'Grower not added', message: friendlyError(e).message })
    }
  }
  const pucsQ = usePucs(form.grower_id || null)
  const pucList = useMemo(() => pucsQ.data ?? [], [pucsQ.data])
  // UF-07: a grower with one PUC gets it filled — once, when the grower is
  // picked (or a draft opens without one); never over a value already there.
  const [pucArmedFor, setPucArmedFor] = useState<string | null>(null)
  const autoPuc = pucAutoFill(pucArmedFor, form.grower_id, form.puc_id, pucsQ.data)
  useEffect(() => {
    if (!autoPuc.settled) return
    setPucArmedFor(null)
    // PUC only: the PUC was empty, so there is no block to drop, and a
    // draft's announced variety must not be wiped by a fill nobody tapped.
    const fill = autoPuc.fill
    if (fill) setForm(f => ({ ...f, puc_id: fill.puc_id, puc: fill.puc_code }))
  }, [autoPuc.settled, autoPuc.fill]) // eslint-disable-line react-hooks/exhaustive-deps
  // …and the grower's last truck is OFFERED (a chip), never filled untapped.
  const lastTruck = useGrowerLastTruck(form.grower_id || null)
  const truckOffer = lastTruckOffer(lastTruck.data, form)
  const { data: blockList  = [] }  = useBlocks(form.puc_id   || null)
  // Is a fruit line drenched? Fruit that never is (stone fruit, mig
  // 20261025191001) isn't asked "Fruit on arrival" and saves as Straight to
  // Cold Room. Worked out per line, so a mixed truck gets both.
  const lineDrench = useLineDrench(form.puc_id || null)
  // bin_type_id → label, so the table's Bin type column can resolve a
  // delivery's custom fruit_bin_type_id (fixed-enum types need no lookup).
  // onlyActive:false so a delivery logged on a since-deactivated bin type still
  // resolves its label instead of reverting to em-dash.
  const { data: binTypes   = [] }  = useBinTypes({ onlyActive: false })
  // Weight + QC at intake (spec #3956): behind the business's switch (J1), so
  // every citrus form is unchanged. The net preview uses the server's rungs
  // (crate type tare x crates); the server stores its own copy.
  const weighAtIntake = capabilityOn(activeTenant?.capabilities?.intake_weight)
  // QC later (mig 20261296000000, Thomas 2 Nov): the check is done after the
  // save, from the QC queue or each pallet, so the form neither asks for a
  // template nor opens the check.
  const qcAtIntake = weighAtIntake && !capabilityOn(activeTenant?.capabilities?.delivery_qc_later)
  const intakeGrossKg = weighAtIntake ? parseKg(form.gross_weight_kg) : null
  const intakeOtherKg = weighAtIntake ? parseKg(form.other_tare_kg) : null
  const intakeCrateKg = crateTareKg(form.fruit_container_type, binTypes.filter(t => t.is_active))
  const intakeNetNow  = intakeNet({ grossKg: intakeGrossKg, otherTareKg: intakeOtherKg, crateTareKg: intakeCrateKg, crates: parseInt(form.bins_received) || 0 })
  const intakeWeightIssue = weighAtIntake
    ? intakeWeightProblem({ grossKg: intakeGrossKg, grossRaw: form.gross_weight_kg, otherTareKg: intakeOtherKg, otherRaw: form.other_tare_kg, net: intakeNetNow })
    : null
  const qcTemplatesQ = useQcPickableTemplates({ enabled: qcAtIntake })
  const intakeQcTemplates = templatesForDelivery(qcTemplatesQ.data ?? [], form.variety || null)
  // Delivery points for the filter facet AND the intake picker. [] for every
  // packhouse that hasn't defined any (all citrus today), which is what keeps
  // both off their screen.
  const { data: deliveryPoints = [] } = useDeliveryPoints()
  // Intake's point, remembered per TENANT: the next truck usually rolls into
  // the same dock, so this is one less tap at the gate. Not part of the intake
  // form state — EMPTY_FORM is re-applied after every save and would wipe it.
  const [deliveryPointId, setDeliveryPointId] = useTenantPersistedState<string>('kf49.deliveries.lastDeliveryPoint', '')
  // Only ever send a point still in the live registry: a remembered id whose
  // point was since deactivated drops out of the picker (showing "not
  // recorded") while the stale id lingers in localStorage — sending it would
  // stamp a point the operator cannot see. Record what is on screen.
  const activeDeliveryPointId = useMemo(
    () => (deliveryPoints.some(p => p.delivery_point_id === deliveryPointId) ? deliveryPointId : null),
    [deliveryPoints, deliveryPointId],
  )
  const binTypesById = useMemo(
    () => new Map(binTypes.map(t => [t.bin_type_id, t.label])),
    [binTypes],
  )

  // The board previously pulled every delivery ever, every 30s (and on every
  // per-row mutation). Default to the last 90 days; the "All history" toggle —
  // or an explicit older "From" date filter — widens the server window so the
  // client-side date filter never shows empty for want of unloaded rows.
  // The rule lives in useHistoryWindow (shared with BlueberryDeliveries and
  // the Receive-pallets register).
  const serverFromIso = useHistoryWindow(showAllHistory, fromFilter)

  const deliveriesQ = useQuery({
    queryKey: ['deliveries', serverFromIso ?? 'all'],
    // Shared pager: default last-90-days window via serverFromIso (full history
    // when the toggle sets it null); drains in bounded pages, no cap-lift.
    queryFn: () => fetchAllDeliveries(serverFromIso, db),
    refetchInterval: POLL.live  // poll every 30s so new WhatsApp drafts appear
  })
  const deliveries = deliveriesQ.data ?? []
  const isLoading = deliveriesQ.isPending && deliveriesQ.isFetching
  // Back from a delivery returns the board to the card it was left at (UF-06).
  const boardRootRef = useRef<HTMLDivElement>(null)
  useRegisterScroll({ key: 'deliveries-board', ready: deliveriesQ.data !== undefined, rootRef: boardRootRef })

  // Per-delivery tipped-kg sum. Kept as a separate query rather than a
  // PostgREST embed on the deliveries select because the embed turned
  // out to drop the whole response when tipping_log's RLS or relation
  // detection mismatched — easier and safer to fetch independently
  // and merge by delivery_id client-side.
  //
  // Note: tipping_log has no deleted_at column (corrections are made
  // by UPDATE not soft-delete), so we don't filter on it. Initial
  // ship of this query did — the filter on a non-existent column
  // silently errored and the kg badge never appeared.
  // Server-side aggregate (mig 618) scoped to the visible delivery_ids, instead
  // of scanning the whole ~1M-row tipping_log ledger and summing in JS (which
  // also hit the bare-select 1000-row cap and silently under-counted). The id
  // list rides in the key so it refetches as the board changes; the
  // ['deliveries-tipped-kg'] prefix invalidation in DeliveryDetail still matches.
  const deliveryIds = useMemo(
    () => Array.from(new Set((deliveries ?? []).map(d => d.delivery_id).filter(Boolean))) as string[],
    [deliveries],
  )
  const { data: tippingByDelivery = { kg: new Map<string, number>(), latest: new Map<string, string>() }, isSuccess: tippingReadOk, isError: tippingReadFailed } = useQuery({
    queryKey: ['deliveries-tipped-kg', deliveryIds],
    enabled: deliveryIds.length > 0,
    queryFn: async () => {
      return withTimeout(readTippingSummaryRows<TippingSummaryRow>(db, deliveryIds), 'Deliveries: delivery_tipping_summary')
    },
    // The cache holds the RPC rows (Maps would not persist offline); same
    // cached shape as DeliveriesReport's query on this key.
    select: tippingFromRows,
    refetchInterval: POLL.live,
  })
  const tippedKgByDelivery   = tippingByDelivery.kg
  const latestTipByDelivery  = tippingByDelivery.latest

  // Per-delivery run allocations. A delivery can be linked to multiple
  // runs via run_deliveries (split tipping); we surface every link so the
  // card can show "RUN-2026-021" badges. Independent query — the embed
  // path is fragile. Scoped to the visible deliveries (.in) so it stops
  // scanning the whole run_deliveries table and silently truncating at 1000
  // rows (which dropped run badges off older deliveries); .range lifts the cap
  // for the bounded set. The id list rides in the key so it refetches with the board.
  const { data: runsByDelivery = new Map<string, RunLink[]>(), isError: runsReadFailed, isSuccess: runsReadOk } = useQuery({
    queryKey: ['deliveries-run-allocations', deliveryIds],
    enabled: deliveryIds.length > 0,
    queryFn: async () => {
      // Chunked: one .in() over a busy board passes the URL limit (U14 D4).
      return readRunAllocationRows<RunDelRow>(db, deliveryIds)
    },
    // Plain rows are cached (a Map would not persist offline); the Map is built here.
    select: selectRunsByDelivery,
    refetchInterval: POLL.live,
  })

  // Per-delivery empties-out loans, batched. Each delivery card's
  // EmptiesOutSummary used to fire its OWN bin_loans query (.in over that one
  // delivery's addition ids) — an N+1 storm of dozens-to-100+ identical-shaped
  // requests on every list render (live module test, 2026-06-25). Hoisted to a
  // single .in over every visible delivery's addition ids, grouped by
  // delivery_id client-side; each card reads its slice from the map. Mirrors
  // runsByDelivery above (independent query — the embed path is fragile).
  const additionToDelivery = useMemo(() => {
    const m = new Map<string, string>()
    for (const d of deliveries ?? []) {
      for (const a of d.delivery_additions ?? []) m.set(a.addition_id, d.delivery_id)
    }
    return m
  }, [deliveries])
  const allAdditionIds = useMemo(() => Array.from(additionToDelivery.keys()), [additionToDelivery])
  const loansSelect = useCallback(
    (rows: EmptiesLoan[]) => loansByDeliveryFromRows(rows, additionToDelivery),
    [additionToDelivery],
  )
  const { data: loansByDelivery = new Map<string, EmptiesLoan[]>(), isError: loansReadFailed } = useQuery({
    queryKey: ['deliveries-empties-loans', allAdditionIds],
    enabled: allAdditionIds.length > 0,
    queryFn: async () => {
      // Chunked: addition ids pass the URL limit sooner than delivery ids (U14 D4).
      return readEmptiesLoanRows<EmptiesLoan>(db, allAdditionIds)
    },
    // Plain rows are cached (a Map would not persist offline); the Map is built here.
    select: loansSelect,
    refetchInterval: POLL.live,
  })

  // Duplicate delivery-note-number guard (KF49 census 2026-09-08: four double
  // captures and sixteen splits sat under a repeated note, all same-day, and
  // the old 24h in-memory line never stopped one). The server is asked whether
  // this grower already used the note this season; the register's loaded
  // window is the fallback when that read fails. A known repeat shows under
  // the field and Save asks before writing a second delivery on the same
  // note — it asks, it never refuses: splits are legitimate.
  const duplicateNote = useDuplicateNote(
    tenantId, form,
    // The register rows carry delivery_note_number; the Delivery type does not declare it.
    deliveries as unknown as readonly DuplicateNoteRow[],
  )

  const drafts = deliveries.filter(d => d.status === 'DRAFT')
  // UF-13: announcements older than 48 h fold away. "Now" is the board's last
  // successful poll (every 30 s), so the split moves on while the tablet sits
  // open all day — a mount-time clock would freeze it.
  const announced = splitAnnouncementsByAge(drafts, deliveriesQ.dataUpdatedAt)
  const [showOlderAnnouncements, setShowOlderAnnouncements] = useState(false)

  // Distinct grower / variety options. Grower options union the master
  // grower registry with the names that appear in existing deliveries —
  // so a newly-added grower shows up in the filter immediately, even
  // before they've had their first delivery. Without this union, the
  // operator who just added "Acme Citrus" wouldn't see them in the
  // dropdown and would assume the create didn't take.
  const growerOptions = useMemo(() => {
    const set = new Set<string>()
    for (const d of deliveries) if (d.status !== 'DRAFT' && d.grower) set.add(d.grower.trim())
    for (const g of growerList) if (g.name) set.add(g.name.trim())
    return Array.from(set).sort((a, b) => a.localeCompare(b))
  }, [deliveries, growerList])
  const varietyOptions = useMemo(() => {
    const set = new Set<string>()
    for (const d of deliveries) if (d.status !== 'DRAFT' && d.variety) set.add(d.variety.trim())
    return Array.from(set).sort((a, b) => a.localeCompare(b))
  }, [deliveries])
  // Block options scoped to the currently-selected grower / variety so the
  // dropdown stays useful — block codes repeat across growers and a single
  // grower's blocks are what the operator actually wants to pick from.
  const blockOptions = useMemo(() => {
    const set = new Set<string>()
    for (const d of deliveries) {
      if (d.status === 'DRAFT' || !d.block) continue
      if (growerFilter  && d.grower.trim()  !== growerFilter)  continue
      if (varietyFilter && d.variety.trim() !== varietyFilter) continue
      set.add(d.block.trim())
    }
    return Array.from(set).sort((a, b) => a.localeCompare(b))
  }, [deliveries, growerFilter, varietyFilter])

  // Needs check is judged against the latest tip. Until that read lands the
  // filter would drop every delivery whose only gap is an unchecked tip, so
  // the board says the read is not done instead of showing a wrong list.
  const tipChecksUnsure = activeFilter === 'NEEDS_CHECK' && deliveryIds.length > 0 && !tippingReadOk

  const active = filterDeliveries(deliveries, {
    statusFilter:  activeFilter,
    latestTipByDelivery: latestTipByDelivery,
    growerFilter,
    varietyFilter,
    blockFilter,
    deliveryPointFilter,
    fromFilter,
    toFilter,
    searchField,
    searchQuery,
  })

  // Used below by the "Clear filters" affordance and the paltrack chip.
  const searchTerm = searchQuery.trim().toLowerCase()

  const paltrackPendingCount = deliveries.filter(d =>
    d.status !== 'DRAFT' && (d as Delivery & { paltrack_status?: string }).paltrack_status === 'pending'
  ).length

  // D3: pending ones OLDER than the window cannot be in the chip's count. Ask,
  // so the chip never reads all clear over paperwork it cannot see. Unknown
  // (loading or failed) is not zero: the chip then says nothing about older.
  const olderQ = useQuery({
    queryKey: ['paltrack-pending-older', tenantId, serverFromIso],
    enabled:  !!tenantId && !!serverFromIso,
    queryFn:  () => withReadTimeout(countOlderPendingPaltrack(db, tenantId!, serverFromIso!), 'Deliveries: older pending paltrack'),
    refetchInterval: POLL.live,
  })
  const olderPending = !serverFromIso ? 0 : olderQ.data   // undefined = not known
  const paltrackChipAllClear = paltrackPendingCount === 0 && olderPending === 0

  // UF-07: the chip's count. The tip half needs the tipping read, so until that
  // read lands the chip shows no number rather than a count that is too low.
  const needsCheckCount = deliveries.filter(d => needsCheck(d, latestTipByDelivery.get(d.delivery_id) ?? null)).length

  const anyExtraFilter = !!(growerFilter || varietyFilter || blockFilter || deliveryPointFilter || fromFilter || toFilter || searchTerm)

  // UF-05: what the "Filters (n)" button counts: the seven folded controls set away from their defaults.
  const foldedFilterCount = [growerFilter, varietyFilter, blockFilter, deliveryPointFilter, fromFilter, toFilter].filter(Boolean).length + (showAllHistory ? 1 : 0)

  // UF-04: the status chip counts as a filter too, so Clear filters resets it.
  const statusChipOn = activeFilter !== 'ALL'
  const statusChipLabel = activeFilter === 'PALTRACK_PENDING' ? 'Paltrack pending' : activeFilter === 'NEEDS_CHECK' ? 'Needs check' : statusTone('delivery', activeFilter).label
  const clearAllFilters = () => {
    setFilter('ALL'); setGrowerFilter(''); setVarietyFilter(''); setBlockFilter(''); setDeliveryPointFilter('')
    setFromFilter(''); setToFilter(''); setSearchQuery('')
  }

  // Render-on-data-presence: no points defined → no facet, so a citrus
  // packhouse sees the filter bar exactly as it was. A still-set filter keeps
  // the facet visible even after the last point is deactivated, so the
  // operator can always see (and clear) what is narrowing their board.
  const showPointFacet = deliveryPoints.length > 0 || !!deliveryPointFilter

  // ---------------------------------------------------------------------------
  // Save new delivery (manual intake)
  // ---------------------------------------------------------------------------
  // Pallet lines of THIS form that a Save already wrote, and the ids a Save
  // minted for them, keyed by the line's own pallet ids (#4009 review). Save
  // tapped again after a lost reply, or after a later line failed, then never
  // writes a second load or a second delivery of the same pallets. Pallet ids
  // are minted per form, so the keys never meet another truck's.
  const palletLinesDone = useRef(new Map<string, { deliveryId: string | null; additionId: string | null }>())
  const palletLineIds   = useRef(new Map<string, { kind: 'new' | 'join'; deliveryId: string; additionId: string }>())

  const saveMutation = useMutation({
    mutationFn: async () => {
      const { data: { session } } = await supabase.auth.getSession(); const user = session?.user
      if (!user) throw new ValidationError('Not authenticated')
      const db = saveDb ?? await ensureSaveDb()
      if (!db) throw new ValidationError('Session not ready — refresh and try again.')

      if (!form.grower_id) throw new ValidationError('Pick the grower from the list — a delivery must belong to a registered grower.')
      // A repeated note # was already asked about, before mutate() (submitCapture).
      // One truck, one or more fruit lines: the main form fields are line 1;
      // extra_lines carry additional varieties / bin types off the same
      // truck. Validate every line up front so nothing writes until the
      // whole load is capturable.
      const lines: IntakeLine[] = [
        {
          block_id: form.block_id, block: form.block,
          variety:  form.variety,  bins:  form.bins_received,
          fruit_container_type: form.fruit_container_type,
          pallets: form.pallets, pallet_tare: form.other_tare_kg,
        },
        ...form.extra_lines,
      ]
      const activeBinTypes = binTypes.filter(t => t.is_active)
      const parsedLines = lines.map((raw, i) => parseIntakeLine(raw, lines.length > 1 ? `Line ${i + 1}: ` : '',
        weighAtIntake ? { crateTareKg: crateTareKg(raw.fruit_container_type, activeBinTypes) } : null))
      // R2: empties Yes/No no longer blocks the delivery — it defaults to No
      // (falsy) and the operator taps Yes when they actually issue empties.
      // Its answers — and the grower bins' — are checked HERE, with the lines,
      // so nothing writes until the whole truck is capturable. They used to be
      // checked after the delivery was committed.
      checkTruckExtras(form, form.grower_id)
      // Line 1's weight (spec #3956). Never sent where the switch is off; with
      // it on, every line weighs its pallets instead (spec #4006).
      const firstLineWeight = !weighAtIntake && intakeGrossKg != null
        ? { gross_weight_kg: intakeGrossKg, other_tare_kg: intakeOtherKg ?? 0 }
        : null

      // Admin back-date: if a date override is set and it's not today,
      // skip the today-window auto-group lookup. The trigger keys
      // client_ref_no off received_at, so back-dating slots the row
      // into the right ISO week / day-of-week sequence automatically.
      // What did NOT record (a photo, the empties, the grower bins), collected
      // across the save and told to the operator once, after the delivery
      // itself has saved (onSuccess).
      const problems: string[] = []
      // `max={todaySast}` on the input is a browser HINT — a typed value, a
      // stale form state or a browser that ignores it all get through, and the
      // insert path has no server-side date guard the way the tip RPCs do (mig
      // 20260625230000 rejects a future p_tipped_at). Refuse here rather than
      // book fruit into the future, where it drops out of every "today" view
      // and lands in a week that has not happened.
      if (canBackDate && form.received_at_override && form.received_at_override > todaySast) {
        throw new ValidationError('Received date cannot be in the future.')
      }
      const overrideDate = canBackDate && form.received_at_override
        ? form.received_at_override
        : ''
      const isBackDated = !!overrideDate && overrideDate !== todaySast
      const receivedAtOverrideIso = overrideDate
        ? `${overrideDate}T12:00:00+02:00`
        : null

      // Ids of the FIRST line's rows — the empties / grower-bins sub-flows
      // after the loop link to them (one truck; the loan and holdings ride
      // with the first load).
      let firstDeliveryId: string | null = null
      let firstAdditionId: string | null = null

      // Deliveries minted by THIS submit — a later line grouping into one of
      // these is genuinely the same truck, so no question needed. Anything
      // OLDER gets an explicit choice below (P0-11).
      const insertedThisSubmit = new Set<string>()
      // What each line became, for the board's saved banner (UF-06).
      const savedLines: SavedIntakeLine[] = []

      for (let i = 0; i < parsedLines.length; i++) {
        const ln   = parsedLines[i]
        const bins = ln.binsNum
        // The operator's "Fruit on arrival" pick, unless this line's fruit is
        // never drenched (then it wasn't asked for this line).
        const intakePath = intakePathForLine(form.intake_path, lineDrench(ln.variety, ln.block_id).usesDrench)
        const newStatus  = initialStatusForPath(intakePath)

        // A pallet line an earlier Save of this form already wrote: skip it.
        const palletKey = ln.pallets ? ln.pallets.map(p => p.pallet_id).join(',') : null
        const done = palletKey ? palletLinesDone.current.get(palletKey) : undefined
        if (done) {
          if (i === 0) { firstDeliveryId = done.deliveryId; firstAdditionId = done.additionId }
          savedLines.push({ deliveryId: done.deliveryId, queued: !done.deliveryId, variety: ln.variety, bins, status: newStatus, joined: undefined })
          continue
        }
        // Ids an earlier Save of this form minted for this line (its reply was
        // lost): reuse them, so the server replays instead of writing again.
        const minted = palletKey ? palletLineIds.current.get(palletKey) : undefined

        // Auto-group: existing confirmed delivery same PUC + variety + block +
        // fruit container + intake path today. A later line of this very
        // submit can group into an earlier line's just-inserted row — same key
        // means it is genuinely the same delivery.
        let existing = !isBackDated && form.puc_id && minted?.kind !== 'new'
          ? await findTodaysDelivery(form.puc_id, ln.variety.trim(), ln.block_id || null, fruitContainerCols(ln.fruit_container_type), intakePath)
          : null
        // A load with pallets joins only a delivery that has pallets (spec
        // #4006 §4.2): the pallets carry every crate, so an unpalleted delivery
        // would lose its own. It saves as its own delivery instead.
        if (existing && ln.pallets && !await deliveryHasPallets(db, existing.delivery_id)) {
          problems.push(`${ln.variety.trim()}: saved as its own delivery. Today's delivery from ${form.puc.trim() || 'this PUC'} was captured without pallets, so these pallets could not join it.`)
          existing = null
        }

        // P0-11 — the July "20 → 22" footgun: a clerk re-entering a corrected
        // count was silently ADDED to the load it was meant to fix (20 → 42).
        // A merge into a delivery this submit did NOT create must be an
        // explicit decision, with the resulting total spelled out.
        if (existing && !insertedThisSubmit.has(existing.delivery_id)) {
          const choice = await choiceDialog({
            title:   'Delivery already captured today',
            message: `${form.puc.trim() || 'This PUC'} · ${ln.variety.trim()} already has a confirmed delivery today with ${existing.bins_received} bins. Adding these ${bins} makes ${existing.bins_received + bins} bins. If you're re-entering to CORRECT a wrong count, don't add — open that delivery and edit it.`,
            tone:    'warning',
            options: [
              { value: 'merge',    label: `Add ${bins} bins — second load (total ${existing.bins_received + bins})` },
              { value: 'separate', label: 'Capture as a separate delivery' },
            ],
          })
          if (choice === null) {
            throw new ValidationError(i === 0
              ? 'Nothing saved. If you were correcting a count, open the existing delivery and edit it.'
              : 'Stopped before this line — earlier lines of this intake were already saved.')
          }
          if (choice === 'separate') existing = null
        }

        let resolvedDeliveryId: string | null = null
        // Addition_id of the row created for *this* line — used to
        // link the bin_loan (empties out) to the specific load it was
        // issued alongside (mig 342). Without this, second-load empties
        // can't be attributed to load 2 and the first load's snapshot
        // gets overwritten.
        let resolvedAdditionId: string | null = null
        let lineQueued = false
        let lineSentId: string | null = null
        let lineJoined: SavedIntakeLine['joined']

        if (existing && ln.pallets) {
          // A load with pallets joining a palleted delivery (spec #4006 §4.2):
          // one call writes the load row and its pallets, numbered on from the
          // last one; the server moves the delivery's crates and weight.
          let joinNotePath: string | null = null
          let joinNotes = form.notes.trim() || null
          if (deliveryNotePhoto) {
            const up = await uploadGatePhoto(db, 'delivery-photos',
              tenantPath(tenantId, `${existing.delivery_id}/note-add-${Date.now()}.jpg`), deliveryNotePhoto)
            joinNotePath = up.path
            if (up.error) {
              problems.push(notePhotoProblem(up.error))
              joinNotes = withPhotoLossNote(joinNotes, 'Delivery-note photo')
            }
          }
          const joinAdditionId = minted?.kind === 'join' && minted.deliveryId === existing.delivery_id ? minted.additionId : newUuid()
          if (palletKey) palletLineIds.current.set(palletKey, { kind: 'join', deliveryId: existing.delivery_id, additionId: joinAdditionId })
          const { error: joinErr } = await withTimeout(db.rpc('add_delivery_load_pallets', {
            p_delivery_id: existing.delivery_id,
            p_addition_id: joinAdditionId,
            p_addition: {
              notes:                   joinNotes,
              delivery_note_number:    form.delivery_note_number.trim() || null,
              delivery_note_photo_url: joinNotePath,
              block_id:                ln.block_id || null,
              block:                   ln.block.trim() || null,
              variety:                 ln.variety.trim(),
              ...fruitContainerCols(ln.fruit_container_type),
            },
            p_pallets: ln.pallets,
          }), 'Deliveries: add_delivery_load_pallets')
          if (joinErr) throw joinErr
          if (i === 0 && photoFile) {
            const up = await uploadGatePhoto(db, 'delivery-photos',
              tenantPath(tenantId, `${existing.delivery_id}/load-${Date.now()}.jpg`), photoFile)
            if (up.error) problems.push(loadPhotoProblem(up.error))
            else if (up.path) {
              try {
                await patchDeliveryAtCurrentVersion(db, existing.delivery_id, { scale_photo_url: up.path, update_reason: 'gate intake — load photo' })
              } catch (e) {
                problems.push(loadPhotoProblem(e))
              }
            }
          }
          resolvedAdditionId = joinAdditionId
          resolvedDeliveryId = existing.delivery_id
          await logStatusChange(existing.delivery_id, newStatus, newStatus, user.id, `Added ${bins} crates on ${ln.pallets.length} pallet(s) (grouped)`)
          lineJoined = {
            loadNo:    await addedLoadNumber(db, existing.delivery_id),
            totalBins: existing.bins_received + bins,
          }
        } else if (existing) {
          // Add to existing delivery — auto-grouped second / third load.
          // Per-load DN# + DN photo are captured on the addition row (mig
          // 208) so the original load's note isn't shadowed by subsequent
          // loads. The parent deliveries.delivery_note_number stays as the
          // first-load reference for back-compat with older readers.
          // The truck's load photo rides with the first line, as the empties
          // do; on a grouped load it replaces the parent's photo (the audit
          // trail keeps the earlier one), the same as Confirm Arrival's merge.
          // The total this load makes, read before anything is written.
          const joinedTotal = existing.bins_received + bins
          // A weighed load adds to a weighed delivery's weight; the server
          // re-works the crate tare and net. An unweighed delivery cannot take
          // a part weight (its net would be wrong), so that is told instead.
          let joinedWeight: Record<string, number> = {}
          if (i === 0 && firstLineWeight) {
            if (existing.gross_weight_kg != null) {
              joinedWeight = {
                gross_weight_kg: Number(existing.gross_weight_kg) + firstLineWeight.gross_weight_kg,
                other_tare_kg:   Number(existing.other_tare_kg ?? 0) + firstLineWeight.other_tare_kg,
              }
            } else {
              problems.push(`The weight (${firstLineWeight.gross_weight_kg} kg) was not added: this load joined a delivery that was not weighed. Open the delivery and weigh it as a whole, or capture this load as a separate delivery next time.`)
            }
          } else if (existing.gross_weight_kg != null) {
            // An unweighed load joining a weighed delivery: the server blanks the
            // net (the gross no longer covers every crate). Say so, and how to fix it.
            problems.push(`The delivery's net weight is now blank: these ${bins} crates joined it without a weight. Weigh the whole delivery and type its gross under Delivery details on its page.`)
          }
          let loadPath: string | null = null
          if (i === 0 && photoFile) {
            const up = await uploadGatePhoto(db, 'delivery-photos',
              tenantPath(tenantId, `${existing.delivery_id}/load-${Date.now()}.jpg`), photoFile)
            loadPath = up.path
            if (up.error) problems.push(loadPhotoProblem(up.error))
          }
          try {
            await updateWithVersion('deliveries',
              { col: 'delivery_id', value: existing.delivery_id },
              existing.version,
              {
                bins_received:  existing.bins_received  + bins,
                bins_remaining: existing.bins_remaining + bins,
                ...joinedWeight,
                ...(loadPath ? { scale_photo_url: loadPath } : {}),
                update_reason:  `auto-group: +${bins} bins (manual intake, second load)`,
              },
            )
          } catch (e) {
            if (isStaleWriteError(e)) {
              throw new ValidationError(
                'Another operator just added bins to this delivery — refresh and re-enter so the totals stay correct.',
              )
            }
            throw e
          }

          // Upload the per-load delivery-note photo first so we have its
          // path before inserting the addition row.
          let additionNotePath: string | null = null
          let additionNotes = form.notes.trim() || null
          if (deliveryNotePhoto) {
            const up = await uploadGatePhoto(db, 'delivery-photos',
              tenantPath(tenantId, `${existing.delivery_id}/note-add-${Date.now()}.jpg`), deliveryNotePhoto)
            // Surface a failed upload rather than saving silently without it.
            // The operator photographed the farmer's paper delivery note at the
            // gate and the UI confirmed it with a ✓ — on an upload timeout the
            // delivery saved with the photo NULL and reported success, so
            // nobody knew. By then the truck has left. This is the primary
            // intake evidence, and the load's notes say it was taken.
            additionNotePath = up.path
            if (up.error) {
              problems.push(notePhotoProblem(up.error))
              additionNotes = withPhotoLossNote(additionNotes, 'Delivery-note photo')
            }
          }

          const { data: addRow, error: addErr } = await withTimeout(db
            .from('delivery_additions')
            .insert({
              delivery_id:             existing.delivery_id,
              bins,
              source:                  'MANUAL',
              added_by:                user.id,
              notes:                   additionNotes,
              delivery_note_number:    form.delivery_note_number.trim() || null,
              delivery_note_photo_url: additionNotePath,
              // This line's own block / variety / container (mig 20261019140000).
              block_id:                ln.block_id || null,
              block:                   ln.block.trim() || null,
              variety:                 ln.variety.trim(),
              ...fruitContainerCols(ln.fruit_container_type),
            })
            .select('addition_id').single(), 'Deliveries: delivery_additions')
          if (addErr) throw addErr
          resolvedAdditionId = (addRow?.addition_id as string | undefined) ?? null
          await logStatusChange(existing.delivery_id, newStatus, newStatus, user.id, `Added ${bins} bins (grouped)`)
          resolvedDeliveryId = existing.delivery_id
          lineJoined = {
            loadNo:    await addedLoadNumber(db, existing.delivery_id),
            totalBins: joinedTotal,
          }
        } else {
          // Create new delivery. The common no-sub-options path routes
          // through insertOrQueueDelivery so a flat Wi-Fi link can't hold
          // the operator at the gate for minutes (cleared the 5-min hang
          // that prompted this overhaul). The rarer toggles (issue
          // empties, track grower bins) need cross-row reads + version
          // checks that don't replay safely from a queue, so they fall
          // back to the inline path below.
          // Mint the ids up front so the save is idempotent — a queue-drain
          // retry replays the SAME ids and the mig-615 RPC no-ops instead of
          // creating a duplicate delivery — and so the inline sub-option flows
          // below can link to the load without an extra round-trip.
          const newDeliveryId = minted?.kind === 'new' ? minted.deliveryId : newUuid()
          const newAdditionId = minted?.kind === 'new' ? minted.additionId : newUuid()
          if (palletKey) palletLineIds.current.set(palletKey, { kind: 'new', deliveryId: newDeliveryId, additionId: newAdditionId })
          // Later lines of this same submit may auto-group into this row —
          // that's the one merge that stays silent (see P0-11 choice above).
          insertedThisSubmit.add(newDeliveryId)
          const deliveryPayload: Record<string, unknown> = {
            delivery_id:    newDeliveryId,
            grower:         form.grower.trim(),
            variety:        ln.variety.trim(),
            puc:            form.puc.trim()        || null,
            block:          ln.block.trim()        || null,
            bins_received:  bins,
            bins_remaining: bins,
            // Optional fruit container (mig 652 + fruit_bin_type_id). Decodes to
            // the enum column OR a custom bin_types FK; both NULL = legacy tare.
            ...fruitContainerCols(ln.fruit_container_type),
            delivery_type:  form.delivery_type,
            intake_path:    intakePath,
            vehicle_reg:    form.vehicle_reg.trim() || null,
            driver_name:    form.driver_name.trim() || null,
            notes:          form.notes.trim()        || null,
            status:         newStatus,
            source:         'MANUAL',
            grower_id:      form.grower_id || null,
            puc_id:         form.puc_id    || null,
            block_id:       ln.block_id    || null,
            // Optional physical intake point (mig 20260727090100). ID ONLY —
            // a BEFORE trigger stamps the delivery_point NAME snapshot from the
            // registry, so a client-sent name would just be overwritten.
            // Truck-level, so every fruit line of this intake shares the point.
            // ⚠️ Sent but NOT stored: the live save_gate_intake_delivery
            // (20261019140000, from 20261019120000's re-emit) lost
            // delivery_point_id from its INSERT, so a new manual intake — live
            // or queued — saves with no point. Owed server-side (a migration).
            delivery_point_id: activeDeliveryPointId,
            uses_kf49_bins: form.uses_kf49_bins,
            received_by:    user.id,
            delivery_note_number: form.delivery_note_number.trim() || null,
            ...(receivedAtOverrideIso ? { received_at: receivedAtOverrideIso } : {}),
            // Intake weight (mig 20261185000000): line 1 only, where the
            // switch is off. With it on, each line's pallets (spec #4006):
            // the server writes them with the delivery and keeps the sums.
            ...(i === 0 && firstLineWeight ? firstLineWeight : {}),
            ...(ln.lineWeight ? ln.lineWeight : {}),
            ...(ln.pallets ? { pallets: ln.pallets } : {}),
          }
          const additionPayload: Record<string, unknown> = {
            addition_id:          newAdditionId,
            bins,
            source:               'MANUAL',
            added_by:             user.id,
            delivery_note_number: form.delivery_note_number.trim() || null,
            // Per-load identity (mig 20261019140000): this line's block,
            // variety and container ride on the load row, so a second block
            // on the truck is never lost behind the parent's.
            block_id:             ln.block_id || null,
            block:                ln.block.trim() || null,
            variety:              ln.variety.trim(),
            ...fruitContainerCols(ln.fruit_container_type),
          }

          // Sub-options force the inline path for the FIRST line only: the
          // empties / grower-bins flows below need that line's resolved ids
          // BEFORE the mutation returns, which only works when the
          // deliveries row is already in the DB. Queueing would defer that
          // to the drainer and leave the sub-option rows orphaned. Later
          // lines never carry sub-options, so they stay queue-eligible.
          const useQueue = !(i === 0 && (form.issue_empties || tracksGrowerBins(form)))
          // The truck's "Take photo of load" photo rides with the first line.
          // It used to be read by Confirm Arrival only, so a manual intake's
          // load photo was shown on screen and never saved anywhere.
          const loadPhoto = i === 0 ? photoFile : null
          if (useQueue) {
            const result = await insertOrQueueDelivery({
              db: db,
              deliveryPayload,
              additionPayload,
              newStatus,
              photo:      deliveryNotePhoto,
              photo_name: deliveryNotePhoto?.name ?? null,
              load_photo:      loadPhoto,
              load_photo_name: loadPhoto?.name ?? null,
            })
            // A photo the server refused (not a network blip — those queue the
            // whole delivery) was dropped; the delivery saved without it.
            if (result.notePhotoError) problems.push(notePhotoProblem(result.notePhotoError))
            if (result.loadPhotoError) problems.push(loadPhotoProblem(result.loadPhotoError))
            if (result.status === 'queued') {
              // Operator sees "Queued — will sync when network is back"
              // via the OfflineQueueBadge that pops up automatically.
              // We can't resolve delivery_id from the queue path; sub-
              // options are gated above, so nothing downstream needs it.
              resolvedDeliveryId = null
              resolvedAdditionId = null
              lineQueued = true
            } else {
              // 'sent' means runDeliveryInsert completed inline. We don't
              // get the ids back from the queue API, but nothing
              // downstream needs them when sub-options are off.
              resolvedDeliveryId = null
              resolvedAdditionId = null
              // It wrote the id minted above — the saved banner links it.
              lineSentId = newDeliveryId
            }
          } else {
            // Inline path (sub-options present). Upload the note photo first
            // (path keyed on the minted id), then one atomic + idempotent RPC
            // (mig 615) writes deliveries + delivery_additions + status_history.
            // Was four sequential writes that could orphan a delivery on a
            // mid-way failure. The empties / grower-bins flows below link to
            // newAdditionId / newDeliveryId.
            // A photo that doesn't upload is told to the operator and noted
            // on the record (it used to be dropped here without a word).
            let firstNotePath: string | null = null
            let inlineDelivery = deliveryPayload
            let inlineAddition = additionPayload
            if (deliveryNotePhoto) {
              const up = await uploadGatePhoto(db, 'delivery-photos',
                tenantPath(tenantId, `${newDeliveryId}/note-${Date.now()}.jpg`), deliveryNotePhoto)
              firstNotePath = up.path
              if (up.error) {
                problems.push(notePhotoProblem(up.error))
                inlineDelivery = { ...inlineDelivery, notes: withPhotoLossNote(inlineDelivery.notes, 'Delivery-note photo') }
                inlineAddition = { ...inlineAddition, notes: withPhotoLossNote(inlineAddition.notes, 'Delivery-note photo') }
              }
            }
            let loadPath: string | null = null
            if (loadPhoto) {
              const up = await uploadGatePhoto(db, 'delivery-photos',
                tenantPath(tenantId, `${newDeliveryId}/load-${Date.now()}.jpg`), loadPhoto)
              loadPath = up.path
              if (up.error) {
                problems.push(loadPhotoProblem(up.error))
                inlineDelivery = { ...inlineDelivery, notes: withPhotoLossNote(inlineDelivery.notes, 'Load photo') }
              }
            }
            const { error } = await withTimeout(db.rpc('save_gate_intake_delivery', {
              p_delivery_id: newDeliveryId,
              p_addition_id: newAdditionId,
              p_delivery:    { ...inlineDelivery, delivery_note_photo_url: firstNotePath },
              p_addition:    { ...inlineAddition, delivery_note_photo_url: firstNotePath },
              p_new_status:  newStatus,
            }), 'Deliveries: save_gate_intake_delivery')
            if (error) throw error
            resolvedAdditionId = newAdditionId
            resolvedDeliveryId = newDeliveryId
            // The RPC has no scale_photo_url column: set it on the row it
            // just wrote. The delivery is saved either way.
            if (loadPath) {
              try {
                await patchDeliveryAtCurrentVersion(db, newDeliveryId, {
                  scale_photo_url: loadPath,
                  update_reason:   'gate intake — load photo',
                })
              } catch (e) {
                problems.push(loadPhotoProblem(e))
              }
            }
          }
        }

        if (i === 0) {
          firstDeliveryId = resolvedDeliveryId
          firstAdditionId = resolvedAdditionId
        }
        if (palletKey) {
          palletLinesDone.current.set(palletKey, {
            deliveryId: lineQueued ? null : (resolvedDeliveryId ?? lineSentId),
            additionId: resolvedAdditionId,
          })
        }
        // Only a QUEUED line has no delivery on the server to link to.
        savedLines.push({
          deliveryId: lineQueued ? null : (resolvedDeliveryId ?? lineSentId),
          queued:     lineQueued,
          variety:    ln.variety,
          bins,
          status:     newStatus,
          joined:     lineJoined,
        })
      }

      // ── Empties + grower bins on the same truck ──
      // After the delivery, linked to the first line's load. Never throws:
      // the delivery is saved, so a failure is told, not raised (the red
      // error used to read as "not saved" and the retry doubled the bins).
      if (form.issue_empties || tracksGrowerBins(form)) {
        problems.push(...await recordTruckExtras({
          db, tenantId, userId: user.id, form,
          growerId:   form.grower_id,
          growerName: form.grower.trim(),
          emptiesNotePhoto,
          deliveryId: firstDeliveryId,
          additionId: firstAdditionId,
          atIso:      receivedAtOverrideIso,
        }))
      }
      const saved: SavedIntake = {
        grower: form.grower,
        note:   form.delivery_note_number,
        lines:  savedLines,
      }
      // The QC check picked on the form runs on line 1's delivery. A queued
      // save has no delivery on the server yet, so it is told instead.
      const qcTemplateId = qcAtIntake ? form.qc_template_id || null : null
      if (qcTemplateId && !savedLines[0]?.deliveryId) {
        problems.push('The QC check did not open: the delivery is saved on this device and sends when the network is back. Open the delivery then and tap Do a QC check.')
      }
      return { problems: [...new Set(problems)], saved, qcTemplateId }
    },
    onSuccess: (res) => {
      qc.invalidateQueries({ queryKey: ['deliveries'] })
      qc.invalidateQueries({ queryKey: ['bin-loans-open'] })
      qc.invalidateQueries({ queryKey: ['bin-loans-history'] })
      qc.invalidateQueries({ queryKey: ['grower-bin-holdings'] })
      // UF-07: the grower's next truck offers THIS one, and a new driver is suggested.
      qc.invalidateQueries({ queryKey: ['grower-last-truck'] })
      qc.invalidateQueries({ queryKey: ['delivery-drivers-history'] })
      // The form resets even when part of the save didn't record: the
      // delivery IS saved, and a full form invites a retry that adds it twice.
      resetCapture()
      setView('board')
      setError(null)
      setJustSaved(res.saved)
      // The intake form's QC pick (spec #3956 §2): straight to the delivery,
      // where the check opens.
      const qcDeliveryId = res.saved.lines[0]?.deliveryId
      if (res.qcTemplateId && qcDeliveryId) {
        navigate(`/deliveries/${qcDeliveryId}?qc=${encodeURIComponent(res.qcTemplateId)}`, { state: returnHere })
      }
      // Not awaited: an alert awaited here keeps the mutation pending, and
      // after 60s SubmitOverlay's stuck-save watchdog would tell the clerk
      // the save "may not have gone through" over a delivery that IS saved
      // — the very prompt to capture it twice.
      if (res.problems.length > 0) {
        void alertDialog({
          title: 'Delivery saved — but not all of it recorded',
          tone: 'danger',
          message: `Don't capture this delivery again.\n\n${res.problems.join('\n\n')}`,
        })
      }
    },
    onError: (e: Error) => setError(e.message)
  })

  // ---------------------------------------------------------------------------
  // Confirm a WhatsApp draft at the gate
  // ---------------------------------------------------------------------------
  const confirmDraftMutation = useMutation({
    mutationFn: async () => {
      if (!draftTarget) throw new ValidationError('No draft selected')
      const { data: { session } } = await supabase.auth.getSession(); const user = session?.user
      if (!user) throw new ValidationError('Not authenticated')

      const bins = parseInt(actualBins)
      if (!bins || bins < 1) throw new ValidationError('Enter the actual bin count')
      // R2: empties Yes/No no longer blocks confirmation (defaults to No).
      // The empties / grower-bin answers are checked below, with the grower,
      // before anything is written.

      const grower    = form.grower.trim()  || draftTarget.grower
      const variety   = form.variety.trim() || draftTarget.variety
      const block     = form.block.trim()   || (draftTarget.block ?? '')
      // Same rule as a manual intake line: fruit that is never drenched
      // wasn't asked, so it saves as Straight to Cold Room.
      const intakePath = intakePathForLine(form.intake_path,
        lineDrench(variety, form.block_id || draftTarget.block_id).usesDrench)
      const newStatus = initialStatusForPath(intakePath)
      const pucId     = form.puc_id         || draftTarget.puc_id
      const growerId  = form.grower_id      || draftTarget.grower_id || null
      if (!growerId) throw new ValidationError('Pick the grower from the list — a delivery must belong to a registered grower.')

      // Confirm Arrival shows the same note #, note photo, back-date, empties
      // and grower-bin fields as Gate Intake, and now records them (it used
      // to write none of them and still tick "Arrival confirmed"). Their
      // checks run here, before anything is written.
      checkTruckExtras(form, growerId)
      if (canBackDate && form.received_at_override && form.received_at_override > todaySast) {
        throw new ValidationError('Received date cannot be in the future.')
      }
      const overrideDate = canBackDate && form.received_at_override ? form.received_at_override : ''
      const isBackDated  = !!overrideDate && overrideDate !== todaySast
      const receivedAtOverrideIso = overrideDate ? `${overrideDate}T12:00:00+02:00` : null
      const noteNumber = form.delivery_note_number.trim() || null
      // A repeated note # was already asked about, before mutate() (submitCapture).

      const db = saveDb ?? await ensureSaveDb()
      if (!db) throw new ValidationError('Session not ready — refresh and try again.')
      // What did NOT record, told once the arrival itself is confirmed.
      const problems: string[] = []

      // Reconciliation note if bin count differs from pre-announced
      const announced = draftTarget.bins_received
      const reconciliationNote = announced > 0 && bins !== announced
        ? `BIN DISCREPANCY: ${announced} announced, ${bins} counted.`
        : null
      let finalNotes = [reconciliationNote, form.notes.trim() || null].filter(Boolean).join(' — ') || null
      // What a MERGE may write onto the existing delivery's notes: this load's
      // own notes, before any photo-loss line. The loss line belongs to this
      // load (its delivery_additions row carries it); written onto the parent
      // it replaced the first load's notes with "Load photo … did not upload".
      const parentNotes = finalNotes

      // Auto-group: check for existing confirmed delivery same PUC + variety
      // + block + fruit container + intake path today. A back-dated arrival
      // never groups into today's delivery (the same rule as Gate Intake).
      let existing = pucId && !isBackDated
        ? await findTodaysDelivery(pucId, variety, form.block_id || draftTarget.block_id || null, fruitContainerCols(form.fruit_container_type), intakePath)
        : null

      // P0-11 — same silent-merge footgun as manual intake: confirming a
      // draft that is really a CORRECTION of an already-confirmed load added
      // its bins on top. Merging must be an explicit decision.
      if (existing) {
        const choice = await choiceDialog({
          title:   'Delivery already captured today',
          message: `${grower} · ${variety} already has a confirmed delivery today with ${existing.bins_received} bins. Confirming this load ADDS its ${bins} bins, making ${existing.bins_received + bins}. If this draft duplicates that delivery, cancel and remove it instead.`,
          tone:    'warning',
          options: [
            { value: 'merge',    label: `Add ${bins} bins — second load (total ${existing.bins_received + bins})` },
            { value: 'separate', label: 'Confirm as a separate delivery' },
          ],
        })
        if (choice === null) throw new ValidationError('Nothing confirmed — the draft stays on the board.')
        if (choice === 'separate') existing = null
      }

      // The photos, once it is known which delivery keeps this load. Neither
      // blocks the confirmation; a failed one is told and noted on the record
      // (the load photo used to drop without a word, the note photo was
      // never uploaded at all).
      const targetId = existing?.delivery_id ?? draftTarget.delivery_id
      let photoUrl: string | null = null
      if (photoFile) {
        const up = await uploadGatePhoto(db, 'delivery-photos',
          tenantPath(tenantId, `${targetId}/${Date.now()}.jpg`), photoFile)
        photoUrl = up.path   // store path, not public URL — bucket is private
        if (up.error) {
          problems.push(loadPhotoProblem(up.error))
          finalNotes = withPhotoLossNote(finalNotes, 'Load photo')
        }
      }
      let notePhotoUrl: string | null = null
      if (deliveryNotePhoto) {
        const up = await uploadGatePhoto(db, 'delivery-photos',
          tenantPath(tenantId, `${targetId}/note-${Date.now()}.jpg`), deliveryNotePhoto)
        notePhotoUrl = up.path
        if (up.error) {
          problems.push(notePhotoProblem(up.error))
          finalNotes = withPhotoLossNote(finalNotes, 'Delivery-note photo')
        }
      }

      const notifyPhone = draftTarget.whatsapp_from
      // The load this arrival became, for the empties loan and the holding.
      let deliveryId: string
      let additionId: string | null

      if (existing) {
        // ── Merge draft into existing confirmed delivery ──

        // Retire the draft FIRST, and only then move the bins. Order is
        // load-bearing: mig 422/447's deliveries_sensitive_col_gate lets only
        // ADMIN / SUPER_ADMIN / MANAGER / FARM_ADMIN / FARM_MANAGER write
        // deleted_at, while the confirm flow itself admits SCALE_OPERATOR and
        // LOADER. With the soft-delete last AND its error discarded, a gate
        // clerk merging a draft added the bins to the parent and left the draft
        // live on the board — the same load counted twice, silently. Doing it
        // first means a denied clerk aborts before any bins move; the load is
        // simply confirmed by a manager instead.
        const { data: retired, error: delErr } = await withTimeout(db.from('deliveries').update({
          deleted_at:     new Date().toISOString(),
          deleted_by:     user.id,
          deleted_reason: `Merged into delivery ${existing.delivery_id}`,
          update_reason:  `merged into ${existing.delivery_id}`,
        }).eq('delivery_id', draftTarget.delivery_id).is('deleted_at', null).select('delivery_id'), 'Deliveries: deliveries')
        if (delErr) {
          if ((delErr as { code?: string }).code === '42501') {
            throw new ValidationError(
              'Only a manager can merge this load into today’s delivery. Ask a manager to confirm it, or confirm it as its own delivery.',
            )
          }
          throw delErr
        }
        // Zero rows = the draft was already merged/deleted by someone else.
        // Adding the bins now would double-count them.
        if ((retired ?? []).length === 0) {
          throw new ValidationError(
            'This draft was already merged by someone else. Refresh the board to see the live delivery.',
          )
        }

        try {
          await updateWithVersion('deliveries',
            { col: 'delivery_id', value: existing.delivery_id },
            existing.version,
            {
              bins_received:  existing.bins_received  + bins,
              bins_remaining: existing.bins_remaining + bins,
              ...(photoUrl    ? { scale_photo_url: photoUrl } : {}),
              ...(parentNotes ? { notes: parentNotes }        : {}),
              update_reason:  `auto-group: +${bins} bins (WhatsApp draft merged)`,
            },
          )
        } catch (e) {
          // The bins never moved, so put the draft back on the board rather
          // than leaving the operator with a load that has vanished.
          const { error: undoErr } = await withTimeout(db.from('deliveries').update({
            deleted_at: null, deleted_by: null, deleted_reason: null,
            update_reason: 'merge aborted — draft restored',
          }).eq('delivery_id', draftTarget.delivery_id), 'Deliveries: deliveries')
          if (undoErr) console.error('draft restore after failed merge:', undoErr)
          if (isStaleWriteError(e)) {
            throw new ValidationError(
              'Another operator just added bins to this delivery — refresh and re-confirm so the totals stay correct.',
            )
          }
          throw e
        }

        // Provenance for the per-load view (and the bin-return trigger, mig
        // 062). supabase-js RESOLVES with { error } — an ignored result loses
        // the load's WhatsApp origin with no sign anything went wrong. The
        // load's own DN # + note photo ride on it (mig 208), as on a grouped
        // Gate Intake load; the parent keeps the first load's note.
        const { data: addRow, error: addErr } = await withTimeout(db.from('delivery_additions').insert({
          delivery_id:             existing.delivery_id,
          bins,
          source:                  'WHATSAPP',
          whatsapp_from:           notifyPhone,
          added_by:                user.id,
          notes:                   finalNotes,
          delivery_note_number:    noteNumber,
          delivery_note_photo_url: notePhotoUrl,
        }).select('addition_id').single(), 'Deliveries: delivery_additions')
        if (addErr) throw addErr
        deliveryId = existing.delivery_id
        additionId = (addRow?.addition_id as string | undefined) ?? null
        await logStatusChange(existing.delivery_id, newStatus, newStatus, user.id, `Added ${bins} bins from WhatsApp draft (grouped)`)

        if (notifyPhone) {
          // Not awaited: the arrival is recorded whatever WhatsApp does. What
          // became of the grower's message lands on the board (waNotice).
          setWaNotice(null)
          void notifyGrower({
            phone: notifyPhone, grower, variety, block, bins,
            intake_path: intakePath,
            delivery_id: existing.delivery_id,
            photo_url: photoUrl ?? undefined,
          }).then(setWaNotice)
        }
      } else {
        // ── Convert draft to confirmed delivery ──
        // Use updateWithVersion against draftTarget.version (snapshot at
        // page render) so a second operator confirming the same draft
        // from a parallel tab can't last-write-wins. The draft row also
        // carries `version` (mig 070-ish) from the bump trigger.
        try {
          await updateWithVersion('deliveries',
            { col: 'delivery_id', value: draftTarget.delivery_id },
            (draftTarget as Delivery & { version?: number }).version ?? 1,
            {
              grower,
              variety,
              puc:            form.puc.trim()          || draftTarget.puc,
              block,
              grower_id:      growerId,
              puc_id:         pucId || null,
              block_id:       form.block_id || draftTarget.block_id || null,
              bins_received:  bins,
              bins_remaining: bins,
              // Optional fruit container (mig 652 + fruit_bin_type_id). Decodes
              // to the enum column OR a custom bin_types FK; both NULL = legacy.
              ...fruitContainerCols(form.fruit_container_type),
              delivery_type:  form.delivery_type,
              intake_path:    intakePath,
              vehicle_reg:    form.vehicle_reg.trim()  || null,
              driver_name:    form.driver_name.trim()  || null,
              notes:          finalNotes,
              status:         newStatus,
              uses_kf49_bins: form.uses_kf49_bins,
              received_by:    user.id,
              // Same rule as the manual payload: id only, the trigger stamps
              // the name (it fires on UPDATE OF delivery_point_id too).
              delivery_point_id: activeDeliveryPointId,
              ...(photoUrl ? { scale_photo_url: photoUrl } : {}),
              // The paper note, never clobbering one the draft already has.
              ...(noteNumber   ? { delivery_note_number:    noteNumber }   : {}),
              ...(notePhotoUrl ? { delivery_note_photo_url: notePhotoUrl } : {}),
              update_reason:  'gate confirmation — WhatsApp draft converted',
            },
          )
        } catch (e) {
          if (isStaleWriteError(e)) {
            throw new ValidationError(
              'This draft was just confirmed by someone else. Refresh the board to see the live row.',
            )
          }
          throw e
        }

        // Error-checked for the same reason as the merge path above: a
        // discarded { error } loses the load's provenance row silently.
        const { data: addRow, error: addErr } = await withTimeout(db.from('delivery_additions').insert({
          delivery_id:             draftTarget.delivery_id,
          bins,
          source:                  'WHATSAPP',
          whatsapp_from:           notifyPhone,
          added_by:                user.id,
          delivery_note_number:    noteNumber,
          delivery_note_photo_url: notePhotoUrl,
        }).select('addition_id').single(), 'Deliveries: delivery_additions')
        if (addErr) throw addErr
        deliveryId = draftTarget.delivery_id
        additionId = (addRow?.addition_id as string | undefined) ?? null
        await logStatusChange(draftTarget.delivery_id, 'DRAFT', newStatus, user.id, 'Gate confirmation')

        // Received on (back-date): its own write, after the confirmation.
        // received_at is a manager-only column on an UPDATE
        // (deliveries_sensitive_col_gate), so a flagged clerk's back-date may
        // be refused — and that must not undo the arrival. Told if refused.
        if (receivedAtOverrideIso) {
          try {
            await patchDeliveryAtCurrentVersion(db, draftTarget.delivery_id, {
              received_at:   receivedAtOverrideIso,
              update_reason: 'gate confirmation — received date set at the gate',
            })
          } catch (e) {
            problems.push(`The received date was NOT changed to ${fmtDateZA(overrideDate)}. ${reasonText(e)} The arrival is confirmed on the day the WhatsApp message came in. A manager can change it under Delivery details (admin) → Edit on the delivery's page.`)
          }
        }

        if (notifyPhone) {
          // Not awaited — see the merge path above.
          setWaNotice(null)
          void notifyGrower({
            phone: notifyPhone, grower, variety, block, bins,
            intake_path: intakePath,
            delivery_id: draftTarget.delivery_id,
            photo_url: photoUrl ?? undefined,
          }).then(setWaNotice)
        }
      }

      // ── Empties + grower bins on the same truck ── now recorded here too
      // (Confirm Arrival showed them and wrote nothing, so a truck that took
      // empties left no loan on the grower). Never throws; see saveMutation.
      if (form.issue_empties || tracksGrowerBins(form)) {
        problems.push(...await recordTruckExtras({
          db, tenantId, userId: user.id, form,
          growerId,
          growerName: grower,
          emptiesNotePhoto,
          deliveryId,
          additionId,
          atIso: receivedAtOverrideIso,
        }))
      }
      return { problems: [...new Set(problems)] }
    },
    onSuccess: (res) => {
      qc.invalidateQueries({ queryKey: ['deliveries'] })
      qc.invalidateQueries({ queryKey: ['bin-loans-open'] })
      qc.invalidateQueries({ queryKey: ['bin-loans-history'] })
      qc.invalidateQueries({ queryKey: ['grower-bin-holdings'] })
      setDraftTarget(null)
      resetCapture()
      setView('board')
      setError(null)
      // Not awaited, for the same reason as the Gate Intake save's alert.
      if (res.problems.length > 0) {
        void alertDialog({
          title: 'Arrival confirmed — but not all of it recorded',
          tone: 'danger',
          message: `Don't confirm or capture this load again.\n\n${res.problems.join('\n\n')}`,
        })
      }
    },
    onError: (e: Error) => setError(e.message)
  })

  // ---------------------------------------------------------------------------
  // Status transition (drenching complete, colour-up return, in-stock)
  // ---------------------------------------------------------------------------
  const transition = useDeliveryTransition()

  // ---------------------------------------------------------------------------
  // Open draft confirmation form pre-filled with WhatsApp data
  // ---------------------------------------------------------------------------
  // Clear everything the capture views hold, so nothing of one truck carries
  // into the next. The load photo, the physical count, the Phytclean override
  // and the variety overrides were never cleared after a manual save or on
  // New, so truck 1's load photo showed on truck 2's blank form. Called on
  // every way out of a capture view (save, confirm, back) and on opening a
  // draft, so New always finds a clean slate.
  function resetCapture() {
    setJustSaved(null)
    setForm(EMPTY_FORM)
    setPucArmedFor(null)
    setDeliveryNotePhoto(null)
    setEmptiesNotePhoto(null)
    setPhotoFile(null)
    // Release the preview's blob (a multi-MB camera capture) — nulling the
    // state does not.
    setPhotoPreview(prev => { if (prev) URL.revokeObjectURL(prev); return null })
    setActualBins('')
    setPhytcleanOverride(false)
    setVarietyOverride(false)
    setLineOverride({})
  }

  // A note this grower already used this season: ask, with the earlier
  // delivery spelled out, before anything is written. Dismiss = nothing saved.
  // An 'unknown' check (offline) never blocks — the advisory under the field
  // already said so. Both saves ask: Confirm Arrival records the note # too.
  async function repeatedNoteAccepted(): Promise<boolean> {
    if (duplicateNote.state !== 'match') return true
    const choice = await choiceDialog({
      title:   'This note number was already used',
      message: `${duplicateNoteSentence(duplicateNote.match, form.delivery_note_number, duplicateNote.others)} A second delivery on the same note is usually a double entry. If this is the same load, don't save it again — open that delivery and edit it.`,
      tone:    'warning',
      options: [
        { value: 'save', label: 'Save as a second delivery on this note' },
        { value: 'back', label: 'Go back and check' },
      ],
    })
    return choice === 'save'
  }

  // Record Delivery / Confirm Arrival. The repeated-note question is asked
  // BEFORE mutate(): SubmitOverlay's 60s stuck-save watchdog starts at
  // mutate(), so a question left open for a minute reported a failed save
  // that had not started. (The merge question still lives in the save: it
  // needs the save's own read of today's deliveries.)
  const asking = useRef(false)
  async function submitCapture(isDraft: boolean) {
    if (asking.current) return
    asking.current = true
    try {
      if (!(await repeatedNoteAccepted())) {
        setError(isDraft
          ? 'Nothing confirmed — the draft stays on the board.'
          : 'Nothing saved. If this is the same load, open the earlier delivery and edit it.')
        return
      }
    } finally {
      asking.current = false
    }
    if (isDraft) confirmDraftMutation.mutate()
    else saveMutation.mutate()
  }

  function openDraftConfirm(draft: Delivery) {
    resetCapture()
    setDraftTarget(draft)
    setForm({
      ...EMPTY_FORM,
      grower_id: draft.grower_id ?? '',
      grower:    draft.grower,
      puc_id:    draft.puc_id   ?? '',
      puc:       draft.puc      ?? '',
      block_id:  draft.block_id ?? '',
      block:     draft.block    ?? '',
      variety:   draft.variety,
      // bins_received left blank — operator physically counts
    })
    if (draft.grower_id && !draft.puc_id) setPucArmedFor(draft.grower_id)
    setView('confirm-draft')
    setError(null)
  }

  // Set the PUC. Blocks belong to a PUC, so the block, its variety and every
  // extra line's block link are dropped (extra lines keep variety/bins).
  function applyPuc(id: string, code: string) {
    setForm(f => ({
      ...f, puc_id: id, puc: code,
      block_id: '', block: '', variety: '',
      extra_lines: f.extra_lines.map(l => ({ ...l, block_id: '', block: '' })),
    }))
  }

  // ---------------------------------------------------------------------------
  // Extra fruit lines (same truck, another variety and/or bin type)
  // ---------------------------------------------------------------------------
  function patchLine(i: number, patch: Partial<IntakeLine>) {
    setForm(f => ({
      ...f,
      extra_lines: f.extra_lines.map((ln, j) => (j === i ? { ...ln, ...patch } : ln)),
    }))
  }

  // ===========================================================================
  // RENDER: Intake form (shared between manual & draft confirm)
  // ===========================================================================
  // The save feedback lives OUTSIDE both views, at the same place in each
  // return, so it survives Record Delivery switching back to the board. Inside
  // the form it unmounted with it, and the tick never painted on the busiest
  // capture on the floor (packhouse audit PM-DLV-12, PW-12).
  const saveOverlays = (
    <>
      <SubmitOverlay
        isPending={saveMutation.isPending}
        isSuccess={saveMutation.isSuccess}
        isError  ={saveMutation.isError}
        error    ={saveMutation.error}
        // A queued save is not on the server yet — don't call it saved.
        successLabel={saveMutation.data && savedIntakeBanner(saveMutation.data.saved).allQueued
          ? 'Saved on this device' : 'Delivery saved'}
        domain   ={DELIVERY_SCREEN_DOMAIN}
        onDismiss={() => saveMutation.reset()}
      />
      <SubmitOverlay
        isPending={confirmDraftMutation.isPending}
        isSuccess={confirmDraftMutation.isSuccess}
        isError  ={confirmDraftMutation.isError}
        error    ={confirmDraftMutation.error}
        successLabel="Arrival confirmed"
        domain   ={DELIVERY_SCREEN_DOMAIN}
        onDismiss={() => confirmDraftMutation.reset()}
      />
    </>
  )

  if (view === 'intake' || view === 'confirm-draft') {
    const isDraft  = view === 'confirm-draft'
    const isPending = saveMutation.isPending || confirmDraftMutation.isPending

    // FR5: Phytclean whitelist verification.
    // A block without `phytclean_cleared` cannot be intake'd for EXPORT without
    // Manager override. Any line's block failing gates the whole intake.
    const lineBlockIds    = [form.block_id, ...form.extra_lines.map(l => l.block_id)].filter(Boolean)
    const phytcleanBlocks = usesPhytclean && form.delivery_type === 'EXPORT' &&
      lineBlockIds.some(id => blockList.find(b => b.block_id === id)?.phytclean_cleared === false)
    const phytcleanGate   = phytcleanBlocks && !phytcleanOverride

    // Fruit on arrival: a line whose fruit is never drenched isn't asked (mig
    // 20261025191001). These are the lines the save walks: a draft is one line,
    // a manual intake is line 1 plus extra_lines.
    const arrivalLines = [
      { variety: form.variety, block_id: form.block_id },
      ...(isDraft ? [] : form.extra_lines),
    ].map(l => lineDrench(l.variety, l.block_id))
    const notDrenched      = arrivalLines.filter(l => !l.usesDrench)
    const askArrival       = notDrenched.length < arrivalLines.length
    const notDrenchedFruit = fruitList(notDrenched.map(l => l.commodity?.name ?? ''))

    return (
      /* h-full (bounded), not min-h-full: with min-h-full the WINDOW scrolls
         (index.css WS-5 note), the inner "scroller" just grows, and the
         sticky save bar below never pinned — Record Delivery only appeared
         after ~13 sections while a truck waited (mobile audit 2026-07-19, S4).
         A definite height makes the flex-1 body a real scroller, the bar pins,
         and body padding handles nav + home-indicator clearance. */
      <>
      <div className="h-full bg-canvas flex flex-col">
        {/* PW-13: title + a one-line subtitle instead of a header wrapped to
            three rows (177px pinned at the keyboard proxy, packhouse audit
            PM-DLV-14). The sender's number leads the line, so a phone's
            truncation eats the words, never the number. */}
        <PageHeader onBack={() => { resetCapture(); setView('board'); setError(null) }}
          title={isDraft ? 'Confirm Arrival' : 'Gate Intake'}
          subtitle={isDraft && draftTarget ? (
            <>
              <MessageCircle size={12} className="inline mr-1 text-green-400" />
              {draftTarget.whatsapp_from} · WhatsApp pre-announcement
            </>
          ) : undefined} />

        <div className="flex-1 min-h-0 p-4 space-y-4 overflow-y-auto pb-8">
          {/* Grower's load photo */}
          {isDraft && draftTarget?.scale_photo_url && (
            <div className="rounded-xl overflow-hidden border border-line">
              <p className="text-caption text-fg-muted font-medium px-3 pt-2 pb-1">📷 Grower's photo</p>
              <a href={draftTarget.scale_photo_url} target="_blank" rel="noopener noreferrer">
                <img
                  src={draftTarget.scale_photo_url}
                  alt="Load photo"
                  className="w-full max-h-56 object-cover"
                />
              </a>
            </div>
          )}

          {/* WhatsApp original message */}
          {isDraft && draftTarget?.whatsapp_raw && (
            <div className="bg-green-900/30 border border-green-800 rounded-xl p-3">
              <p className="text-caption text-green-400 font-medium mb-1">Original WhatsApp message</p>
              <p className="text-sm text-green-200 italic">"{draftTarget.whatsapp_raw}"</p>
            </div>
          )}

          {/* Said BEFORE Confirm, so nobody tells the driver "they'll get a message". */}
          {isDraft && draftTarget?.whatsapp_from && wa.paused && (
            <WhatsAppPausedNote detail="Confirm Arrival still records the load, but the grower will not get a WhatsApp confirmation." />
          )}

          {/* Grower — type to find (UF-07), from the growers table */}
          <div>
            <p className="label">Grower <span className="text-danger">*</span></p>
            <SearchableSelect label="Grower" placeholder="Select grower…"
              value={form.grower_id}
              options={growerList.map(g => ({ value: g.grower_id, label: g.name }))}
              onChange={id => {
                if (id === form.grower_id) return
                const name = growerList.find(g => g.grower_id === id)?.name ?? ''
                // extra_lines reset too — their blocks/varieties belonged to
                // the previous grower.
                setForm(f => ({ ...f, grower_id: id, grower: name, puc_id: '', puc: '', block_id: '', block: '', variety: '', extra_lines: [] }))
                setPucArmedFor(id || null)
              }} />
            {canAddGrower && (
              <button type="button" className="btn-ghost btn-sm mt-1" onClick={addGrowerFromForm}>
                <Plus size={14} /> Add grower
              </button>
            )}
          </div>

          {/* PUC — filtered by grower */}
          <div>
            <label className="label">PUC / Farm</label>
            <select className="field" value={form.puc_id} disabled={!form.grower_id}
              onChange={e => {
                const id = e.target.value
                setPucArmedFor(null)   // the operator chose; never refill over it
                applyPuc(id, pucList.find(p => p.puc_id === id)?.puc_code ?? '')
              }}>
              <option value="">{form.grower_id ? 'Select PUC…' : 'Select grower first'}</option>
              {pucList.map(p => (
                <option key={p.puc_id} value={p.puc_id}>
                  {p.puc_code}{p.farm_name ? ` — ${p.farm_name}` : ''}
                </option>
              ))}
            </select>
          </div>

          {/* Block — filtered by PUC, auto-fills variety */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <label className="label">Block</label>
              <select className="field" value={form.block_id} disabled={!form.puc_id}
                onChange={e => {
                  const id = e.target.value
                  const block = blockList.find(b => b.block_id === id)
                  setForm(f => ({
                    ...f,
                    block_id: id,
                    block: block?.block_name ?? '',
                    variety: block?.variety ?? f.variety
                  }))
                  setVarietyOverride(false)
                }}>
                <option value="">{form.puc_id ? 'Select block…' : 'Select PUC first'}</option>
                {blockList.map(b => (
                  <option key={b.block_id} value={b.block_id}>
                    {b.block_name}{b.variety ? ` (${b.variety})` : ''}
                  </option>
                ))}
              </select>
            </div>

            {/* Variety — auto-filled from block, editable */}
            <VarietyFromBlockField
              id="variety" required
              value={form.variety} onChange={v => setForm(f => ({ ...f, variety: v }))}
              options={varietyLookup.data ?? []}
              blockVariety={blockList.find(b => b.block_id === form.block_id)?.variety ?? null}
              override={varietyOverride} onOverride={setVarietyOverride} />
          </div>

          {/* Delivery point — WHERE this truck offloaded (mig 20260727090100).
              Truck-level, so every fruit line of the intake carries the same
              one. OPTIONAL and self-hiding: DeliveryPointPicker renders null
              until the tenant defines points, so a packhouse that doesn't work
              this way (every citrus one today) sees no new field and nothing
              new can block the save — the column is NULLable on purpose. */}
          <div>
            <DeliveryPointPicker
              value={activeDeliveryPointId}
              onChange={id => setDeliveryPointId(id ?? '')}
            />
          </div>

          {/* FR5: Phytclean not-cleared warning */}
          {phytcleanBlocks && (
            <div className="rounded-xl bg-danger-soft border border-danger-edge px-4 py-3 space-y-2">
              <div className="flex items-start gap-2">
                <AlertTriangle size={18} className="text-danger shrink-0 mt-0.5" />
                <div className="text-sm text-danger">
                  <p className="font-semibold">Block not cleared on Phytclean whitelist</p>
                  <p className="text-caption text-danger mt-0.5">
                    This block is currently not cleared for export. Manager authorisation is required to accept this load.
                  </p>
                </div>
              </div>
              <label className="flex items-center gap-2 text-sm text-danger">
                <input
                  type="checkbox"
                  checked={phytcleanOverride}
                  onChange={e => setPhytcleanOverride(e.target.checked)}
                  className="w-4 h-4 accent-red-500"
                />
                Manager override — accept at own risk
              </label>
            </div>
          )}

          {/* Pallets off the truck (spec #4006): where the business weighs at
              intake, line 1's crates are its pallets' sum. */}
          {weighAtIntake && !isDraft ? (
            <IntakePalletsField idPrefix="line-1"
              rows={form.pallets} palletTare={form.other_tare_kg} crateTareKg={intakeCrateKg}
              onChange={(rows, crates) => setForm(f => ({ ...f, pallets: rows, bins_received: crates ? String(crates) : '' }))}
              onPalletTare={raw => setForm(f => ({ ...f, other_tare_kg: raw }))} />
          ) : (
          /* Actual bin count — operator physically counts */
          <div>
            <label className="label">
              Actual Bin Count (physically counted) <span className="text-danger">*</span>
            </label>
            {isDraft && draftTarget && draftTarget.bins_received > 0 && (
              <p className="text-caption text-fg-muted mb-1.5">
                Grower pre-announced: <span className="font-semibold tabular-nums text-fg">{draftTarget.bins_received} bins</span>
              </p>
            )}
            <input className="field text-2xl font-bold" type="number" inputMode="numeric"
              placeholder="Count the bins…"
              value={isDraft ? actualBins : form.bins_received}
              onChange={e => isDraft
                ? setActualBins(e.target.value)
                : setForm(f => ({ ...f, bins_received: e.target.value }))} />
            {/* Reconciliation warning */}
            {isDraft && draftTarget && draftTarget.bins_received > 0 && parseInt(actualBins) > 0 &&
              parseInt(actualBins) !== draftTarget.bins_received && (
              <div className="mt-2 flex items-start gap-2 bg-warning-soft border border-warning-edge rounded-xl px-3 py-2">
                <AlertTriangle size={16} className="text-warning shrink-0 mt-0.5" />
                <p className="text-sm text-warning">
                  Discrepancy: <span className="font-bold tabular-nums">{draftTarget.bins_received} announced</span> vs{' '}
                  <span className="font-bold tabular-nums">{actualBins} counted</span>. This will be logged automatically.
                </p>
              </div>
            )}
          </div>
          )}

          {/* Gate photo */}
          <div>
            <label className="label">Gate Photo (optional)</label>
            <input
              ref={cameraRef}
              type="file"
              accept="image/*"
              capture="environment"
              className="hidden"
              onChange={e => {
                const file = e.target.files?.[0] ?? null
                setPhotoFile(file)
                // Revoke the PREVIOUS preview before minting a new one. A blob
                // URL pins its Blob until revokeObjectURL — nulling state or
                // unmounting does not release it — and a tablet-camera capture
                // is multi-MB before compression. The gate takes one per
                // delivery all shift with no page reload (standalone PWA), so
                // the leak accumulates for the tab's whole lifetime.
                setPhotoPreview(prev => {
                  if (prev) URL.revokeObjectURL(prev)
                  return file ? URL.createObjectURL(file) : null
                })
              }}
            />
            {photoPreview ? (
              <div className="relative rounded-xl overflow-hidden">
                <img src={photoPreview} alt="Gate photo" className="w-full max-h-48 object-cover" />
                <button
                  onClick={() => { setPhotoFile(null); setPhotoPreview(null) }}
                  className="absolute top-0 right-0 min-w-[44px] min-h-[44px] flex items-center justify-center text-fg">
                  <span className="bg-canvas/70 rounded-full p-1.5 inline-flex">✕</span>
                </button>
              </div>
            ) : (
              <button
                type="button"
                onClick={() => cameraRef.current?.click()}
                className="w-full rounded-xl border-2 border-dashed border-line py-6 flex flex-col items-center gap-2
                           text-fg-muted hover:border-green-500 hover:text-success transition-colors">
                <Camera size={28} />
                <span className="text-sm">Take photo of load</span>
              </button>
            )}
          </div>

          {/* Extra fruit lines — a second variety and/or bin type off the
              same truck. Each saves as its own delivery (auto-grouped
              independently) so tipping tare + run allocation stay correct
              per variety/container; truck-level fields (grower, vehicle,
              note #, photos, empties) are shared from this form. WhatsApp
              drafts stay single-line. */}
          {!isDraft && (
            <div className="space-y-3">
              {form.extra_lines.map((ln, i) => (
                <div key={i} className="rounded-xl border border-line bg-surface/40 p-3 space-y-3">
                  <div className="flex items-center justify-between">
                    <p className="text-sm font-semibold text-fg">Variety / bin type {i + 2}</p>
                    <button type="button"
                      onClick={() => setForm(f => ({ ...f, extra_lines: f.extra_lines.filter((_, j) => j !== i) }))}
                      className="min-w-[44px] min-h-[44px] -my-2 inline-flex items-center justify-center text-fg-muted hover:text-danger"
                      aria-label={`Remove line ${i + 2}`}>
                      <X size={16} />
                    </button>
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <div>
                      <label className="label">Block</label>
                      <select className="field" value={ln.block_id} disabled={!form.puc_id}
                        onChange={e => {
                          const id = e.target.value
                          const block = blockList.find(b => b.block_id === id)
                          patchLine(i, {
                            block_id: id,
                            block:    block?.block_name ?? '',
                            variety:  block?.variety ?? ln.variety,
                          })
                        }}>
                        <option value="">{form.puc_id ? 'Select block…' : 'Select PUC first'}</option>
                        {blockList.map(b => (
                          <option key={b.block_id} value={b.block_id}>
                            {b.block_name}{b.variety ? ` (${b.variety})` : ''}
                          </option>
                        ))}
                      </select>
                    </div>
                    <VarietyFromBlockField
                      id={`variety-line-${i}`} required
                      value={ln.variety} onChange={v => patchLine(i, { variety: v })}
                      options={varietyLookup.data ?? []}
                      blockVariety={blockList.find(b => b.block_id === ln.block_id)?.variety ?? null}
                      override={!!lineOverride[i]} onOverride={v => setLineOverride(o => ({ ...o, [i]: v }))} />
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    {weighAtIntake ? (
                      <div className="col-span-2">
                        <IntakePalletsField idPrefix={`line-${i + 2}`}
                          rows={ln.pallets} palletTare={ln.pallet_tare}
                          crateTareKg={crateTareKg(ln.fruit_container_type, binTypes.filter(t => t.is_active))}
                          onChange={(rows, crates) => patchLine(i, { pallets: rows, bins: crates ? String(crates) : '' })}
                          onPalletTare={raw => patchLine(i, { pallet_tare: raw })} />
                      </div>
                    ) : (
                    <div>
                      <label className="label">Bins <span className="text-danger">*</span></label>
                      <input className="field text-lg font-bold" type="number" inputMode="numeric"
                        placeholder="Count…"
                        value={ln.bins} onChange={e => patchLine(i, { bins: e.target.value })} />
                    </div>
                    )}
                    <div>
                      <label className="label">Arrived in</label>
                      <ContainerTypePicker
                        value={ln.fruit_container_type}
                        onChange={v => patchLine(i, { fruit_container_type: v })}
                        growerId={form.grower_id || null}
                        noneLabel="Not specified — use default tare" />
                    </div>
                  </div>
                </div>
              ))}
              <button type="button"
                onClick={() => setForm(f => ({ ...f, extra_lines: [...f.extra_lines, EMPTY_LINE] }))}
                className="w-full rounded-xl border-2 border-dashed border-line py-3 flex items-center justify-center gap-2
                           text-sm text-fg-muted hover:border-green-500 hover:text-success transition-colors">
                <Plus size={16} /> Add another variety / bin type on this truck
              </button>
            </div>
          )}

          {/* Delivery type */}
          <div>
            <label className="label">Delivery Type</label>
            <div className="grid grid-cols-3 gap-2">
              {(['EXPORT', 'LOCAL', 'IMPORT'] as const).map(t => (
                <button key={t}
                  className={clsx('rounded-xl py-3 font-semibold text-sm transition-colors',
                    form.delivery_type === t
                      ? 'bg-accent text-on-fill'
                      : 'bg-raised text-fg-secondary hover:bg-raised-hover')}
                  onClick={() => setForm(f => ({ ...f, delivery_type: t }))}>
                  {DELIVERY_TYPE_LABEL[t]}
                </button>
              ))}
            </div>
          </div>

          {/* KF49 bins toggle — drives auto bin-loan returns */}
          <div>
            <label className="label">Bins used</label>
            <div className="grid grid-cols-2 gap-2">
              <button
                type="button"
                onClick={() => setForm(f => ({ ...f, uses_kf49_bins: true }))}
                className={clsx('rounded-xl py-3 font-semibold text-sm transition-colors',
                  form.uses_kf49_bins
                    ? 'bg-accent text-on-fill'
                    : 'bg-raised text-fg-secondary hover:bg-raised-hover')}>
                Packhouse bins
              </button>
              <button
                type="button"
                onClick={() => setForm(f => ({ ...f, uses_kf49_bins: false }))}
                className={clsx('rounded-xl py-3 font-semibold text-sm transition-colors',
                  !form.uses_kf49_bins
                    ? 'bg-accent text-on-fill'
                    : 'bg-raised text-fg-secondary hover:bg-raised-hover')}>
                Grower's own bins
              </button>
            </div>
            <p className="text-caption text-fg-muted mt-1.5">
              {form.uses_kf49_bins
                ? `→ ${totalBinsOf(form) || 'N'} bins will auto-return to the grower's open loan`
                : '→ No bin-loan changes — grower supplied their own bins'}
            </p>
          </div>

          {/* Fruit container type (optional, mig 652). Drives the per-bin tare
              the tipping net-weight uses. Leave on "Not specified" to keep the
              legacy resolution (grower override → system default). Pick a light
              container (e.g. arm-lugs) so tipping doesn't over-deduct 37 kg. */}
          <div>
            <label className="label">
              Fruit arrived in{form.extra_lines.length > 0 && ' — first variety'}{' '}
              <span className="text-fg-muted font-normal">(optional)</span>
            </label>
            <ContainerTypePicker
              value={form.fruit_container_type}
              onChange={v => setForm(f => withFruitContainer(f, v))}
              growerId={form.grower_id || null}
              noneLabel="Not specified — use default tare" />
            <p className="text-caption text-fg-muted mt-1">
              {form.fruit_container_type
                ? "→ Tipping uses this container's tare (set it under Config → Storage → Bin types)."
                : '→ Tipping uses the grower / system default tare.'}
            </p>
          </div>

          {/* Weight + QC at intake (spec #3956, J1: the intake_weight switch). */}
          {weighAtIntake && (
            <div className="space-y-3">
              {/* A manual intake weighs its pallets above (spec #4006); a
                  confirmed WhatsApp draft keeps the single weight. */}
              {isDraft && (<>
              <div className="grid grid-cols-2 gap-3">
                <label className="block">
                  <span className="label">
                    Gross weight (kg){form.extra_lines.length > 0 && ' — first variety'}{' '}
                    <span className="text-fg-muted font-normal">(optional)</span>
                  </span>
                  <input className="field" type="text" inputMode="decimal" autoComplete="off"
                    value={form.gross_weight_kg}
                    onChange={e => setForm(f => ({ ...f, gross_weight_kg: e.target.value }))}
                    placeholder="e.g. 640" />
                </label>
                <label className="block">
                  <span className="label">Pallet / other tare (kg)</span>
                  <input className="field" type="text" inputMode="decimal" autoComplete="off"
                    value={form.other_tare_kg}
                    onChange={e => setForm(f => ({ ...f, other_tare_kg: e.target.value }))}
                    placeholder="0" />
                </label>
              </div>
              {intakeWeightIssue ? (
                <p className="text-caption text-danger">{intakeWeightIssue}</p>
              ) : intakeGrossKg != null && (
                <p className="text-caption text-fg-secondary tabular-nums">
                  {intakeNetNow
                    ? `Net ≈ ${intakeNetNow.netKg} kg (${parseInt(form.bins_received) || 0} × crate tare ${intakeCrateKg} kg${intakeOtherKg ? ` + ${intakeOtherKg} kg pallet` : ''})`
                    : !form.fruit_container_type || intakeCrateKg == null
                      ? 'Pick the crate type above (with its tare) to work out the net.'
                      : 'Type the number of crates to work out the net.'}
                </p>
              )}
              </>)}
              {qcAtIntake && <label className="block">
                <span className="label">QC check on this delivery <span className="text-fg-muted font-normal">(optional)</span></span>
                <select className="field text-sm" value={form.qc_template_id}
                  onChange={e => setForm(f => ({ ...f, qc_template_id: e.target.value }))}>
                  <option value="">No QC check now</option>
                  {intakeQcTemplates.map(t => <option key={t.template_id} value={t.template_id}>{t.name}</option>)}
                </select>
                <span className="block text-caption text-fg-muted mt-1">
                  {form.qc_template_id
                    ? '→ The check opens on the delivery as soon as it saves.'
                    : '→ You can also do one later from the delivery.'}
                </span>
              </label>}
            </div>
          )}

          {/* Intake path — not asked for fruit that is never drenched */}
          {askArrival ? (
            <div>
              <label className="label">Fruit on arrival</label>
              <div className="space-y-2">
                {INTAKE_PATH_OPTIONS.map(({ value, label }) => (
                  <button key={value}
                    type="button"
                    className={clsx('w-full rounded-xl py-3 px-4 text-left font-medium text-sm transition-colors',
                      form.intake_path === value
                        ? 'bg-accent text-on-fill'
                        : 'bg-raised text-fg-secondary hover:bg-raised-hover')}
                    onClick={() => setForm(f => ({ ...f, intake_path: value }))}>
                    {label}
                  </button>
                ))}
              </div>
              <p className="text-caption text-fg-muted mt-1.5">
                {INTAKE_PATH_OPTIONS.find(o => o.value === form.intake_path)?.help}
              </p>
              {notDrenched.length > 0 && (
                <p className="text-caption text-fg-muted mt-1.5">
                  {`${notDrenchedFruit} on this truck aren't drenched and go straight to the cold room. This choice is for the rest of the load.`}
                </p>
              )}
            </div>
          ) : (
            <div>
              <p className="label">Fruit on arrival</p>
              <div className="rounded-xl surface py-3 px-4">
                <p className="font-medium text-sm text-fg">Straight to Cold Room</p>
                <p className="text-caption text-fg-muted mt-0.5">
                  {`${notDrenchedFruit} aren't drenched, so this load goes straight to In Stock.`}
                </p>
              </div>
            </div>
          )}

          {/* Vehicle + Driver */}
          <div className="grid grid-cols-2 gap-3">
            <ComboField
              id="vehicle_reg" label="Vehicle Reg"
              value={form.vehicle_reg} onChange={v => setForm(f => ({ ...f, vehicle_reg: v }))}
              options={vehicleRegs.data ?? []} placeholder="CA 123-456" />
            <ComboField
              id="driver_name" label="Driver Name"
              value={form.driver_name} onChange={v => setForm(f => ({ ...f, driver_name: v.slice(0, 100) }))}
              options={driverNames.data ?? []} placeholder="Driver" />
          </div>
          {truckOffer && (
            <button type="button"
              className="min-h-[44px] w-full text-left px-3 rounded-lg border hairline surface text-sm text-fg-secondary hover:text-fg"
              onClick={() => setForm(f => ({
                ...f,
                vehicle_reg: truckOffer.vehicle_reg || f.vehicle_reg,
                driver_name: truckOffer.driver_name || f.driver_name,
              }))}>
              ↻ {truckOffer.text} <span className="text-fg-muted">(tap to use)</span>
            </button>
          )}

          {/* Delivery note from the farmer — number + optional photo. The
              admin completes the Paltrack receipt + client ref later from
              the delivery detail page. */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="label">Delivery note #</label>
              <input className="field" placeholder="From the paper note" maxLength={50}
                value={form.delivery_note_number}
                onChange={e => setForm(f => ({ ...f, delivery_note_number: e.target.value }))} />
              <DuplicateNoteAdvisory dup={duplicateNote} note={form.delivery_note_number} />
            </div>
            <div>
              <label className="label">Note photo</label>
              {deliveryNotePhoto ? (
                <button type="button"
                  onClick={() => setDeliveryNotePhoto(null)}
                  className="field w-full text-left text-success truncate">
                  ✓ {deliveryNotePhoto.name} (tap to remove)
                </button>
              ) : (
                <label className="field w-full flex items-center justify-center text-fg-muted hover:text-success cursor-pointer">
                  <input type="file" accept="image/*" capture="environment" className="hidden"
                    onChange={e => setDeliveryNotePhoto(e.target.files?.[0] ?? null)} />
                  📷 Capture
                </label>
              )}
            </div>
          </div>

          {/* Back-date control. Admins always; non-admin operators with
              the can_override_delivery_date flag (set in Users page) too.
              Empty = use server NOW(). When the chosen date is not today,
              auto-grouping is skipped and the row gets its own client_ref_no
              slot for that day. */}
          {canBackDate && (
            <div>
              <label className="label">
                Received on <span className="text-fg-muted">(can back-date)</span>
              </label>
              <input type="date" className="field" max={todaySast}
                value={form.received_at_override}
                onChange={e => setForm(f => ({ ...f, received_at_override: e.target.value }))} />
              {form.received_at_override && form.received_at_override !== todaySast && (
                <p className="text-caption text-warning mt-1">
                  Back-dated — auto-grouping with today's deliveries is disabled. Client ref slots into that day's sequence.
                </p>
              )}
            </div>
          )}

          {/* Notes */}
          <div>
            <label className="label">Notes</label>
            <textarea className="field resize-none" rows={2} placeholder="Any notes..." maxLength={2000}
              value={form.notes} onChange={e => setForm(f => ({ ...f, notes: e.target.value }))} />
          </div>

          {/* Issue empties on the same truck. Pre-fills grower / vehicle /
              driver from above. Saves into bin_loans alongside the
              delivery insert so they share an issued_at when admin
              back-dates. The signed-note photo is optional for everyone (R4,
              2026-07-06); the note NUMBER is the traceability key. */}
          <div className={clsx(
            'rounded-xl border p-3 space-y-2',
            form.issue_empties === null
              ? 'border-warning-edge bg-warning-soft'
              : 'border-line-subtle bg-canvas/40',
          )}>
            <div className="flex items-center justify-between gap-3 flex-wrap">
              <span className="text-sm font-semibold text-fg">
                Issue empties on this truck?
              </span>
              <div className="flex gap-2">
                <button type="button"
                  onClick={() => setForm(f => ({ ...f, issue_empties: false }))}
                  className={clsx(
                    'px-6 py-3 rounded-xl text-base font-bold border-2 min-w-[5rem]',
                    form.issue_empties === false
                      ? 'bg-raised text-fg border-gray-500'
                      : 'bg-transparent text-fg-muted border-line hover:border-gray-500',
                  )}>
                  No
                </button>
                <button type="button"
                  onClick={() => setForm(f => ({ ...f, issue_empties: true }))}
                  className={clsx(
                    'px-6 py-3 rounded-xl text-base font-bold border-2 min-w-[5rem]',
                    form.issue_empties === true
                      ? 'bg-emerald-600 text-on-fill border-emerald-400'
                      : 'bg-transparent text-fg-muted border-line hover:border-emerald-500',
                  )}>
                  Yes
                </button>
              </div>
            </div>
            {form.issue_empties === null && (
              <p className="text-caption text-warning">Choose Yes or No to continue.</p>
            )}

            {form.issue_empties && (
              <div className="space-y-2 pt-1">
                <p className="text-caption text-fg-muted">
                  Going to <span className="text-fg-secondary font-medium">{form.grower || '(grower not picked)'}</span>
                  {form.vehicle_reg && <> · {form.vehicle_reg}</>}
                  {form.driver_name && <> · {form.driver_name}</>}
                </p>

                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <label className="label">Empty bins</label>
                    <input type="number" inputMode="numeric"
                      className="field text-lg font-bold"
                      value={form.empties_bin_count}
                      onChange={e => setForm(f => ({ ...f, empties_bin_count: e.target.value }))} />
                  </div>
                  <div>
                    <label className="label">Bin type</label>
                    <ContainerTypePicker
                      value={form.empties_container_type}
                      onChange={v => setForm(f => ({ ...f, empties_container_type: v }))}
                      growerId={form.grower_id || null} />
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <label className="label">Delivery note #</label>
                    <input className="field text-sm" maxLength={50}
                      placeholder="From the loan note"
                      value={form.empties_delivery_note_number}
                      onChange={e => setForm(f => ({ ...f, empties_delivery_note_number: e.target.value }))} />
                  </div>
                  <div>
                    {/* R4 made the photo optional for everyone; the red *
                        this carried for operators was never enforced. A
                        caption, not a <label>: the Capture control below is
                        its own label. */}
                    <p className="label">Note photo (optional)</p>
                    {emptiesNotePhoto ? (
                      <button type="button"
                        onClick={() => setEmptiesNotePhoto(null)}
                        className="field w-full text-left text-success truncate">
                        ✓ {emptiesNotePhoto.name} (tap to remove)
                      </button>
                    ) : (
                      <label className="field w-full flex items-center justify-center text-fg-muted hover:text-success cursor-pointer">
                        <input type="file" accept="image/*" capture="environment" className="hidden"
                          onChange={e => setEmptiesNotePhoto(e.target.files?.[0] ?? null)} />
                        📷 Capture
                      </label>
                    )}
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Track grower-owned bins held at the packhouse. Only relevant
              when the grower brought their own bins (uses_kf49_bins=false).
              Records into grower_bin_holdings linked to this delivery. */}
          {!form.uses_kf49_bins && (
            <div className="rounded-xl border border-line-subtle bg-canvas/40 p-3 space-y-2">
              <div className="flex items-center justify-between gap-3">
                <span className="text-sm font-semibold text-fg">Track grower bins held?</span>
                <div className="flex gap-1">
                  <button type="button"
                    onClick={() => setForm(f => ({ ...f, track_grower_bins: false }))}
                    className={clsx(
                      'px-3 py-1 rounded-lg text-caption font-semibold border',
                      !form.track_grower_bins
                        ? 'bg-raised text-fg border-line'
                        : 'bg-transparent text-fg-muted border-line hover:border-line-strong',
                    )}>
                    No
                  </button>
                  <button type="button"
                    onClick={() => setForm(f => ({
                      ...f,
                      track_grower_bins: true,
                      grower_bin_count: f.grower_bin_count || (totalBinsOf(f) ? String(totalBinsOf(f)) : ''),
                    }))}
                    className={clsx(
                      'px-3 py-1 rounded-lg text-caption font-semibold border',
                      form.track_grower_bins
                        ? 'bg-amber-600 text-on-fill border-amber-500'
                        : 'bg-transparent text-fg-muted border-line hover:border-amber-600',
                    )}>
                    Yes
                  </button>
                </div>
              </div>

              {form.track_grower_bins && (
                <div className="space-y-2 pt-1">
                  <p className="text-caption text-fg-muted">
                    These bins stay with us until the grower fetches them. View under
                    <span className="text-fg-secondary font-medium"> Bin Inventory → Grower bins</span>.
                  </p>
                  <div className="grid grid-cols-2 gap-2">
                    <div>
                      <label className="label">Bins held</label>
                      <input type="number" inputMode="numeric"
                        className="field text-lg font-bold"
                        value={form.grower_bin_count}
                        onChange={e => setForm(f => ({ ...f, grower_bin_count: e.target.value }))} />
                    </div>
                    <div>
                      <label className="label">Bin type</label>
                      <ContainerTypePicker
                        value={form.grower_bin_container_type}
                        onChange={v => setForm(f => ({ ...f, grower_bin_container_type: v, grower_bin_type_picked: true }))}
                        growerId={form.grower_id || null} />
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}

          {error && (
            <div className="rounded-xl bg-danger-soft border border-danger-edge px-4 py-3 text-sm text-danger">
              {error}
            </div>
          )}

          {/* Sticky action bar — keeps the record action reachable without
              scrolling to the bottom of this long gate-intake form (a truck is
              waiting). Surfaces the required "Issue empties?" Yes/No here so the
              operator sees the requirement at the action point instead of
              tapping into a ValidationError ~13 sections down. */}
          <div className="sticky bottom-0 z-20 -mx-4 px-4 py-3 bg-canvas/95 backdrop-blur border-t border-line-subtle space-y-2">
            {!isDraft && form.issue_empties === null && (
              <p className="text-caption text-warning text-center">
                Choose <b>“Issue empties on this truck?”</b> (Yes / No) above before recording.
              </p>
            )}
            <Button
              variant="primary"
              className="w-full disabled:cursor-not-allowed"
              disabled={isPending || phytcleanGate || (!isDraft && form.issue_empties === null)}
              onClick={() => { void submitCapture(isDraft) }}>
              {isPending
                ? 'Saving…'
                : phytcleanGate
                  ? '⚠ Manager override required (Phytclean)'
                  : isDraft
                    ? '✓ Confirm Arrival'
                    : form.extra_lines.length > 0
                      ? `✓ Record ${form.extra_lines.length + 1} Deliveries`
                      : '✓ Record Delivery'}
            </Button>
          </div>
        </div>

      </div>
      {saveOverlays}
      </>
    )
  }

  // ===========================================================================
  // RENDER: Main status board
  // ===========================================================================
  const filters = ['ALL', 'RECEIVED', 'AWAITING_DRENCH', 'DRENCHED', 'OUT_FOR_COLOUR_UP', 'IN_STOCK', 'TIPPED']

  return (
    <>
    <div ref={boardRootRef} className="min-h-full bg-canvas flex flex-col">
      {/* Header */}
      {/* The page's buttons go in `actions`, not children: children stay in the
          pinned title row at every width, and beside W-14's Go to button they
          wrapped the pinned header to 175px on a phone (re-render N14). Report
          and Receive packed pallets sit behind More: as worded buttons all four
          wrapped the pinned row to 135px at 768, and as icons named only by a
          tooltip they were unreadable on touch. */}
      <PageHeader title="Deliveries"
        actions={<>
          <PalletViewToggle value={boardLayout} onChange={setBoardLayout} />
          <MoreMenu items={[
            { label: 'Report', icon: Printer, onSelect: () => navigate('/deliveries/report') },
            { label: 'Receive packed pallets', icon: PackagePlus, onSelect: () => navigate('/deliveries/receive') },
          ]} />
          <Button variant="primary" size="sm" aria-label="New delivery" data-guide="deliveries.new" onClick={() => { resetCapture(); setView('intake'); setError(null) }}
            className="flex items-center gap-1.5"
            title="New delivery">
            <Plus size={16} /> New
          </Button>
        </>} />

      {/* What Record Delivery just saved, and the next step (UF-06). */}
      {justSaved && (() => {
        const b = savedIntakeBanner(justSaved)
        return (
          <div className="px-4 pt-3">
            <SavedBanner onDismiss={() => setJustSaved(null)}
              actions={b.canPutOnRun && (
                <SavedBannerLink to="/runs" state={returnHere}>Put on a run →</SavedBannerLink>
              )}>
              <p className="font-semibold">{b.title}</p>
              <ul className="mt-1 space-y-1">
                {b.lines.map((l, i) => (
                  <li key={i} className="flex flex-wrap items-center gap-x-2 gap-y-1">
                    <span>{l.text}</span>
                    {l.openId && (
                      <SavedBannerLink to={`/deliveries/${l.openId}`} state={returnHere}>Open</SavedBannerLink>
                    )}
                    {/* The intake sticker for this delivery's fruit (spec #3956 §3):
                        every packhouse (Thomas, 28 Oct), one per pallet where it has pallets. */}
                    {l.openId && (
                      <SavedBannerLink to={`/delivery-label/${l.openId}`} state={returnHere}>Print sticker</SavedBannerLink>
                    )}
                  </li>
                ))}
              </ul>
              {b.queuedNote && <p className="mt-1 text-caption text-warning">{b.queuedNote}</p>}
            </SavedBanner>
          </div>
        )
      })()}

      {/* From farms — wagons a linked FARM tenant has sent and nobody has
          answered yet. Presence-gated inside the component (FC-39): a packhouse
          nobody sends loads to never sees it, and it is NOT gated on the VEG
          flow — a citrus packhouse whose grower runs the farm app gets real
          pending loads too. Ordered above the WhatsApp pre-announcements
          because this is fruit already at the gate, not fruit on its way. */}
      <PendingFarmLoads tenantId={tenantId} />

      {/* What became of the last gate confirmation's WhatsApp to the grower. */}
      {waNotice && (
        <div className="px-4 pt-3 flex items-start gap-2">
          {waNotice.outcome === 'paused'
            ? <WhatsAppPausedNote className="flex-1" detail="The grower was not sent the gate confirmation." />
            : (
              <p role="status" className={clsx('flex-1 rounded-lg border px-3 py-2 text-caption',
                waNotice.outcome === 'sent'
                  ? 'border-green-800 bg-success-soft text-success'
                  : 'border-warning-edge bg-warning-soft text-warning')}>
                {waNotice.outcome === 'sent'
                  ? 'Gate confirmation sent to the grower on WhatsApp.'
                  // No answer is not a refusal: it may have gone out.
                  : waNotice.outcome === 'unknown'
                    ? "Couldn't confirm whether the grower's WhatsApp went out."
                    : `The grower's WhatsApp confirmation was not sent: ${waNotice.message}`}
              </p>
            )}
          <button type="button" aria-label="Dismiss WhatsApp notice" onClick={() => setWaNotice(null)}
            className="min-h-[44px] min-w-[44px] flex items-center justify-center rounded-lg text-fg-muted hover:text-fg">
            <X size={16} />
          </button>
        </div>
      )}

      {/* WhatsApp Drafts — shown prominently at top */}
      {drafts.length > 0 && (
        <div className="bg-surface border-b border-line p-4">
          <p className="text-sm font-semibold text-green-400 flex items-center gap-2 mb-3">
            <MessageCircle size={16} />
            WhatsApp Pre-Announcements ({drafts.length})
          </p>
          {wa.paused && (
            <WhatsAppPausedNote className="mb-3" detail="Confirming one still records the arrival, but the grower gets nothing back on WhatsApp." />
          )}
          <div className="space-y-2">
            {announced.recent.map(d => (
              <AnnouncementCard key={d.delivery_id} d={d} onConfirm={openDraftConfirm}
                onNotComing={canMarkNotComing ? x => void notComing.ask(x) : undefined}
                notComingPending={notComing.isPending} />
            ))}
          </div>
          {/* UF-13: older than 48 h folds behind a counted toggle — never
              hidden without a trace; Confirm is unchanged inside. */}
          {announced.older.length > 0 && (
            <div className={clsx(announced.recent.length > 0 && 'mt-2')}>
              <button type="button"
                aria-expanded={showOlderAnnouncements}
                aria-controls="older-announcements"
                onClick={() => setShowOlderAnnouncements(v => !v)}
                className="min-h-[44px] w-full flex items-center gap-2 rounded-lg px-2 text-left text-sm text-fg-secondary hover:text-fg hover:bg-raised/60">
                <ChevronRight size={16} className={clsx('shrink-0 transition-transform', showOlderAnnouncements && 'rotate-90')} />
                {announced.older.length} announced more than 48 h ago
              </button>
              {showOlderAnnouncements && (
                <div id="older-announcements" className="space-y-2 mt-2">
                  {announced.older.map(d => (
                    <AnnouncementCard key={d.delivery_id} d={d} onConfirm={openDraftConfirm}
                onNotComing={canMarkNotComing ? x => void notComing.ask(x) : undefined}
                notComingPending={notComing.isPending} />
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* Field-scoped search — the shared row (also on the Receive-pallets register) */}
      <FieldScopedSearch
        fields={SEARCH_FIELDS}
        field={searchField}
        onField={setSearchField}
        query={searchQuery}
        onQuery={setSearchQuery}
        noun="deliveries"
      />

      {/* Status filter tabs + Paltrack-pending chip + weekly link */}
      <div className="flex gap-2 px-4 py-3 overflow-x-auto scrollbar-hide border-b border-line-subtle items-center">
        {filters.map(f => (
          <button key={f}
            onClick={() => setFilter(f)}
            className={clsx(
              'shrink-0 rounded-full px-3 py-1.5 text-caption font-semibold transition-colors',
              activeFilter === f
                ? 'bg-accent text-on-fill'
                : 'bg-raised text-fg-secondary hover:bg-raised-hover'
            )}>
            {f === 'ALL' ? 'All' : statusTone('delivery', f).label}
          </button>
        ))}
        {/* Paltrack-pending — visually separated; shows the count of
            deliveries waiting on admin to fill in receipt + client ref. */}
        <span className="text-fg-muted px-1">·</span>
        <button
          onClick={() => setFilter('PALTRACK_PENDING')}
          className={clsx(
            'shrink-0 rounded-full px-3 py-1.5 text-caption font-semibold transition-colors flex items-center gap-1.5',
            activeFilter === 'PALTRACK_PENDING'
              ? 'bg-amber-600 text-on-fill'
              : !paltrackChipAllClear
                ? 'bg-warning-soft text-warning hover:bg-amber-900'
                : 'bg-raised text-fg-secondary',
          )}>
          Paltrack pending
          {paltrackPendingCount > 0 && (
            <span className={clsx(
              'rounded-full px-1.5 text-caption',
              activeFilter === 'PALTRACK_PENDING' ? 'bg-amber-800' : 'bg-amber-700/60'
            )}>{paltrackPendingCount}</span>
          )}
          {!!olderPending && (
            <span className="text-caption font-normal" title="Pending Paltrack older than the board's window. Turn on All history under Filters to list them.">
              {paltrackPendingCount > 0 ? `· ${olderPending} older` : `${olderPending} older`}
            </span>
          )}
        </button>
        {canSignOff && (
          <button
            onClick={() => setFilter(activeFilter === 'NEEDS_CHECK' ? 'ALL' : 'NEEDS_CHECK')}
            aria-pressed={activeFilter === 'NEEDS_CHECK'}
            className={clsx(
              'tap-target-y shrink-0 rounded-full px-3 py-1.5 text-caption font-semibold transition-colors flex items-center gap-1.5',
              activeFilter === 'NEEDS_CHECK'
                ? 'bg-amber-600 text-on-fill'
                : needsCheckCount > 0 && tippingReadOk
                  ? 'bg-warning-soft text-warning hover:bg-amber-900'
                  : 'bg-raised text-fg-secondary',
            )}>
            Needs check
            {tippingReadOk && needsCheckCount > 0 && (
              <span className={clsx(
                'rounded-full px-1.5 text-caption',
                activeFilter === 'NEEDS_CHECK' ? 'bg-amber-800' : 'bg-amber-700/60'
              )}>{needsCheckCount}</span>
            )}
          </button>
        )}
        <button
          onClick={() => navigate('/deliveries/weekly')}
          className="shrink-0 ml-auto text-caption text-link hover:text-blue-300 underline-offset-4 hover:underline whitespace-nowrap min-h-[44px] inline-flex items-center px-2">
          Weekly summary →
        </button>
      </div>

      {/* Grower / variety / block / date filters */}
      <div className="px-4 py-2 border-b border-line-subtle bg-canvas/40">
        {/* UF-05: the seven filters fold behind "Filters (n)"; search and status chips stay open. */}
        <FilterFold foldAtAllWidths activeFilters={foldedFilterCount}>
          <div className="grid grid-cols-2 sm:grid-cols-5 gap-2">
        <label className="block">
          <span className="text-caption uppercase tracking-wider text-fg-muted">Grower</span>
          <select value={growerFilter} onChange={e => { setGrowerFilter(e.target.value); setBlockFilter('') }}
            className="w-full mt-0.5 bg-surface border border-line rounded-lg px-2 py-1 text-caption text-fg">
            <option value="">All</option>
            {growerOptions.map(g => <option key={g} value={g}>{g}</option>)}
          </select>
        </label>
        <label className="block">
          <span className="text-caption uppercase tracking-wider text-fg-muted">Variety</span>
          <select value={varietyFilter} onChange={e => { setVarietyFilter(e.target.value); setBlockFilter('') }}
            className="w-full mt-0.5 bg-surface border border-line rounded-lg px-2 py-1 text-caption text-fg">
            <option value="">All</option>
            {varietyOptions.map(v => <option key={v} value={v}>{v}</option>)}
          </select>
        </label>
        <label className="block">
          <span className="text-caption uppercase tracking-wider text-fg-muted">Block</span>
          <select value={blockFilter} onChange={e => setBlockFilter(e.target.value)}
            className="w-full mt-0.5 bg-surface border border-line rounded-lg px-2 py-1 text-caption text-fg">
            <option value="">All</option>
            {blockOptions.map(b => <option key={b} value={b}>{b}</option>)}
          </select>
        </label>
        {showPointFacet && (
          <label className="block">
            <span className="text-caption uppercase tracking-wider text-fg-muted">Delivery point</span>
            <select value={deliveryPointFilter} onChange={e => setDeliveryPointFilter(e.target.value)}
              className="w-full mt-0.5 bg-surface border border-line rounded-lg px-2 py-1 text-caption text-fg">
              <option value="">All</option>
              {deliveryPoints.map(p => (
                <option key={p.delivery_point_id} value={p.name}>{p.code ? `${p.code} · ${p.name}` : p.name}</option>
              ))}
              {/* Legacy + citrus rows carry no point; this bucket is how the
                  operator reaches them once the facet exists. */}
              <option value={UNSPECIFIED_DELIVERY_POINT}>Unspecified</option>
            </select>
          </label>
        )}
        <label className="block">
          <span className="text-caption uppercase tracking-wider text-fg-muted">From</span>
          <input type="date" value={fromFilter} onChange={e => setFromFilter(e.target.value)}
            className="w-full mt-0.5 bg-surface border border-line rounded-lg px-2 py-1 text-caption text-fg" />
        </label>
        <label className="block">
          <span className="text-caption uppercase tracking-wider text-fg-muted">To</span>
          <input type="date" value={toFilter} onChange={e => setToFilter(e.target.value)}
            className="w-full mt-0.5 bg-surface border border-line rounded-lg px-2 py-1 text-caption text-fg" />
        </label>
        <label className="block">
          <span className="text-caption uppercase tracking-wider text-fg-muted">History</span>
          <button
            onClick={() => setShowAllHistory(v => !v)}
            title={showAllHistory
              ? 'Showing all deliveries'
              : 'Showing the last 90 days — older deliveries are hidden until you set an earlier From date or switch to All history'}
            className={clsx(
              'w-full mt-0.5 rounded-lg px-2 py-1 text-caption font-semibold transition-colors border',
              showAllHistory
                ? 'bg-amber-600 border-amber-500 text-on-fill'
                : 'bg-surface border-line text-fg-secondary hover:bg-raised',
            )}>
            {showAllHistory ? 'All history' : 'Last 90 days'}
          </button>
        </label>
          </div>
        </FilterFold>
        {/* UF-04: say what the board shows. Only over a confirmed read: a
            pending or failed read must not claim "0 of 0". */}
        {deliveriesQ.isSuccess && tipChecksUnsure && (
          <p className="text-caption text-fg-muted" data-testid="board-tip-checks-unsure" role="status">
            {tippingReadFailed
              ? 'Tip checks could not be read. Needs check cannot be judged yet. Try again in a moment.'
              : 'Tip checks are still loading. Needs check cannot be judged yet.'}
          </p>
        )}
        {deliveriesQ.isSuccess && !tipChecksUnsure && (
          <div className="flex flex-wrap items-center gap-x-3 gap-y-1" data-testid="board-summary">
            <span className="text-caption text-fg-muted">
              {boardSummary(deliveries, active.length, statusChipOn ? statusChipLabel : null, showAllHistory)}
            </span>
            {(anyExtraFilter || statusChipOn) && (
              <button onClick={clearAllFilters}
                className="min-h-[44px] px-2 text-caption text-link hover:text-blue-300 underline">
                Clear filters
              </button>
            )}
          </div>
        )}
      </div>

      {/* Delivery cards */}
      <div className="flex-1 p-4 space-y-3">
        {/* Gate the empty branch: pending/paused/error used to render the
            "No deliveries" + New-delivery CTA — the idle-wake audit's
            duplicate-capture invitation (UI-4). Only confirmed success may
            claim the list is empty. */}
        {active.length === 0 && !tipChecksUnsure && (
          <QueryStateGate query={deliveriesQ} noun="deliveries" skeleton={{ rows: 4, rowClassName: 'h-28 rounded-xl' }}
            empty={(anyExtraFilter || statusChipOn) ? (
              <DeliveriesNoMatch
                windowFromIso={showAllHistory ? null : serverFromIso}
                searchTerm={searchTerm}
                onShowAllHistory={() => setShowAllHistory(true)}
                onClear={clearAllFilters} />
            ) : (
              <TeachingEmptyState icon={Truck} status="No deliveries"
                cue="Once fruit arrives at the gate, log it here so tipping has a delivery to draw against."
                action={
                  <Button variant="primary" size="sm"
                    onClick={() => { resetCapture(); setView('intake'); setError(null) }}
                    className="flex items-center gap-1.5">
                    <Plus size={14} /> New delivery
                  </Button>
                } />
            )} />
        )}

        {/* Excel-style grid — same rows, scannable as a spreadsheet. */}
        {!isLoading && !tipChecksUnsure && active.length > 0 && boardLayout === 'table' && (
          <DeliveriesTable
            rows={active.map(d => toDeliveryRow(d, tippedKgByDelivery.get(d.delivery_id) ?? 0, binTypesById))}
            windowFromIso={serverFromIso}
            renderAction={row => {
              const d = active.find(x => x.delivery_id === row.delivery_id)
              if (!d) return null
              // F01 UF-01: a delivery with no forward step shows UF-02's Put on a run,
              // under the same condition as the chip (a good read of the run links, none found).
              const canPutOnRun = runsReadOk && !runsReadFailed
                && (runsByDelivery.get(d.delivery_id)?.length ?? 0) === 0
                && (d.status === 'IN_STOCK' || d.status === 'RECEIVED')
              return (
                <div className="flex flex-col items-start gap-2">
                  <DeliveryActionButtons delivery={d} {...transition.propsFor(d)}
                    onTransition={(to, reason) => transition.mutate(d, to, reason)} />
                  {canPutOnRun && <PutOnRunButton delivery={d} />}
                </div>
              )
            }}
          />
        )}

        {boardLayout === 'cards' && !tipChecksUnsure && active.map(d => (
          <DeliveryCard
            key={d.delivery_id}
            delivery={d}
            kgTipped={tippedKgByDelivery.get(d.delivery_id) ?? 0}
            latestTipAt={latestTipByDelivery.get(d.delivery_id) ?? null}
            runLinks={runsByDelivery.get(d.delivery_id) ?? []}
            loans={loansByDelivery.get(d.delivery_id) ?? []}
            runsReadFailed={runsReadFailed && !runsByDelivery.has(d.delivery_id)}
            runsPending={!runsReadOk && !runsReadFailed}
            loansReadFailed={loansReadFailed}
            transitionBusy={transition.propsFor(d).busy}
            transitionSaving={transition.propsFor(d).saving}
            transitionError={transition.propsFor(d).error}
            transitionDone={transition.propsFor(d).done}
            windowFromIso={serverFromIso}
            onTransition={(to, reason) => transition.mutate(d, to, reason)}
          />
        ))}
      </div>
    </div>
    {saveOverlays}
    {notComing.overlay}
    </>
  )
}

// ---------------------------------------------------------------------------
// Delivery Card with expandable loads breakdown
// ---------------------------------------------------------------------------
export function DeliveryCard({
  delivery: d,
  kgTipped,
  latestTipAt,
  runLinks,
  loans,
  runsReadFailed = false,
  runsPending = false,
  loansReadFailed = false,
  transitionBusy = false,
  transitionSaving = transitionBusy,
  transitionError = null,
  transitionDone = null,
  windowFromIso = null,
  onTransition
}: {
  delivery: Delivery
  kgTipped: number
  /** Max(tipped_at) across this delivery's tipping_log rows. NULL if no
   *  tips yet. Used to detect "a new tip arrived since the last admin
   *  tipping-check" so the badge can flip from green ✓ to amber New. */
  latestTipAt: string | null
  /** Every run this delivery has been linked to (via run_deliveries).
   *  Empty array means unallocated. */
  runLinks: { run_id: string; run_number: string; status: string }[]
  /** This delivery's empties-out loans, sliced from the parent's batched
   *  loansByDelivery query (avoids a per-card bin_loans N+1). */
  loans: EmptiesLoan[]
  /** The run-allocation read failed: do not claim Unallocated. */
  runsReadFailed?: boolean
  /** The run-allocation read has not answered yet: say nothing about runs. */
  runsPending?: boolean
  /** The empties read failed: do not show a confident absence. */
  loansReadFailed?: boolean
  /** A status write is running on the board: lock the action buttons. */
  transitionBusy?: boolean
  transitionSaving?: boolean
  /** The last status tap on this delivery failed. */
  transitionError?: string | null
  /** The last status tap on this delivery worked: the status it moved to. */
  transitionDone?: string | null
  /** D3: the board's window, stamped on the View link so the page's "(N left)" counts the same set. */
  windowFromIso?: string | null
  onTransition: (to: DeliveryStatus, reason: string) => void
}) {
  const navigate = useNavigate()
  const [runsSheet, setRunsSheet] = useState(false)
  // Berry fruit is fed by cooling batches, never linked to a run: no chip.
  const usesForceCooling = useFlowManifest().usesForceCooling
  const qc = useQueryClient()
  const { data: me } = useCurrentUser()
  const canEditDn = isManagerOrAbove(me?.role)
  const [expanded, setExpanded] = useState(false)
  // Per-load inline DN edit state. addition_id of the load currently in
  // edit-mode + the text being typed.
  const [editingAdditionId, setEditingAdditionId] = useState<string | null>(null)
  const [editingDn, setEditingDn] = useState('')
  const [savingDn, setSavingDn] = useState(false)

  const pct = d.bins_received > 0
    ? Math.round(((d.bins_received - d.bins_remaining) / d.bins_received) * 100)
    : 0
  const loads      = d.delivery_additions ?? []
  const loadCount  = loads.length
  const isGrouped  = loadCount > 1
  // Walk loads in insertion order for per-load DN display in the breakdown.
  const orderedLoads = [...loads].sort((a, b) => a.added_at.localeCompare(b.added_at))

  // Save the typed DN onto a specific delivery_additions row. Also patches
  // the parent deliveries.delivery_note_number when the edited row is the
  // FIRST load (back-compat with anything still reading the parent column).
  async function saveAdditionDn(addition: DeliveryAddition, isFirstLoad: boolean) {
    setSavingDn(true)
    try {
      const trimmed = editingDn.trim()
      const { error } = await supabase.from('delivery_additions')
        .update({ delivery_note_number: trimmed || null })
        .eq('addition_id', addition.addition_id)
      if (error) throw error
      if (isFirstLoad) {
        // Best-effort parent sync — failure here doesn't undo the addition
        // update because the addition row is the authoritative source.
        // updateWithVersion + a version re-read so we don't stomp a
        // concurrent status transition; stale-write is treated as a
        // benign skip (the audit trail still has the addition update).
        try {
          const { data: parent } = await supabase.from('deliveries')
            .select('version')
            .eq('delivery_id', addition.delivery_id)
            .maybeSingle()
          const parentVersion = (parent as { version?: number } | null)?.version
          if (parentVersion != null) {
            await updateWithVersion('deliveries',
              { col: 'delivery_id', value: addition.delivery_id },
              parentVersion,
              {
                delivery_note_number: trimmed || null,
                update_reason:        'DN edit on first-load addition (parent sync)',
              },
            )
          }
        } catch (e) {
          if (!isStaleWriteError(e)) throw e
          console.warn('Parent DN sync stale-write; addition is authoritative.')
        }
      }
      qc.invalidateQueries({ queryKey: ['deliveries'] })
      setEditingAdditionId(null)
      setEditingDn('')
    } catch (e) {
      await alertDialog({ title: "Couldn't save DN", message: (e as Error).message, tone: 'danger' })
    } finally {
      setSavingDn(false)
    }
  }
  // Build the DN list for the card header. Mig 208's per-addition snapshot
  // is the authoritative per-load source, but its backfill only filled the
  // FIRST addition of each pre-mig-208 delivery, so:
  //   - Use each load's own delivery_note_number when present.
  //   - For the FIRST load, fall back to deliveries.delivery_note_number
  //     (the parent column the legacy UI still writes alongside).
  //   - Subsequent loads with NULL stay NULL — surfaced as "no DN" / "+ add DN"
  //     in the breakdown so the operator can backfill inline.
  const parentDn = (d as Delivery & { delivery_note_number?: string | null }).delivery_note_number
  const perLoadDns = orderedLoads.map((l, idx) =>
    l.delivery_note_number ?? (idx === 0 ? parentDn ?? null : null),
  )
  const loadDns = perLoadDns.filter((s): s is string => !!s && s.trim() !== '')
  // De-duplicate while preserving order in case two loads ended up with
  // the same DN# (operator typo we don't want to amplify visually).
  const uniqueDns = Array.from(new Set(
    loadDns.length > 0
      ? loadDns
      : (parentDn && parentDn.trim() !== '' ? [parentDn] : []),
  ))

  return (
    <div {...registerRow(d.delivery_id)} className="card space-y-0 p-0 overflow-hidden">
      {/* Clickable header */}
      <button
        className="w-full text-left p-4 space-y-3"
        onClick={() => isGrouped && setExpanded(e => !e)}
      >
        {/* Top row */}
        <div className="flex items-start justify-between gap-3">
          <div className="flex-1 min-w-0">
            <p className="font-bold text-fg text-base">
              {d.grower}
              {(d as Delivery & { client_ref_no?: string | null }).client_ref_no && (
                <span className="text-caption font-mono text-success ml-2">{(d as Delivery & { client_ref_no?: string | null }).client_ref_no}</span>
              )}
            </p>
            <p className="text-sm text-fg-secondary">{d.variety}
              {d.puc   && <span className="text-fg-muted"> · PUC {d.puc}</span>}
              {d.block && <span className="text-fg-muted"> · {d.block}</span>}
            </p>
            {(uniqueDns.length > 0 || d.driver_name) && (
              <p className="text-caption text-fg-muted mt-0.5 flex items-center gap-1 flex-wrap">
                <FileText size={10} />
                {uniqueDns.length > 0 ? (
                  <span className="font-mono text-fg-secondary">
                    DN {uniqueDns.join(', ')}
                  </span>
                ) : (
                  <span className="italic">no DN#</span>
                )}
                {d.driver_name && <span>· {d.driver_name}</span>}
              </p>
            )}

            {/* Run-allocation badges. Always rendered so operators can see at
                a glance which deliveries are still floating + which are tied
                to a run. Click a badge → jump straight to the run detail. */}
            <div className="mt-1 flex items-center gap-1.5 flex-wrap">
              {!d.grower_id && d.status !== 'DRAFT' && (
                <span className="inline-flex items-center gap-1 text-caption uppercase tracking-wider rounded-full px-1.5 py-0.5 font-semibold bg-warning-soft text-warning border border-warning-edge"
                  title="Not linked to a registered grower — open the delivery and link it.">
                  <AlertTriangle size={9} /> Unlinked grower
                </span>
              )}
              {runLinks.length === 0 ? (
                runsReadFailed && (d.status === 'IN_STOCK' || d.status === 'RECEIVED') ? (
                  <span className="inline-flex items-center gap-1 text-caption uppercase tracking-wider rounded-full px-1.5 py-0.5 font-semibold bg-warning-soft text-warning border border-warning-edge"
                    title="The run links could not be read. This delivery may already be on a run. Check before you add it to one.">
                    <AlertTriangle size={9} /> Run links not read
                  </span>
                ) : !runsPending && !usesForceCooling && (d.status === 'IN_STOCK' || d.status === 'RECEIVED') ? (
                  <button type="button"
                    onClick={e => { e.stopPropagation(); setRunsSheet(true) }}
                    className="inline-flex items-center gap-1 min-h-[44px] text-caption uppercase tracking-wider rounded-full px-3 font-semibold bg-warning-soft text-warning border border-warning-edge">
                    <AlertTriangle size={9} /> Put on a run
                  </button>
                ) : null
              ) : (
                runLinks.map(rl => (
                  <button
                    key={rl.run_id}
                    onClick={e => { e.stopPropagation(); navigate(`/runs/${rl.run_id}`) }}
                    className={clsx(
                      'inline-flex items-center gap-1 text-caption uppercase tracking-wider rounded-full px-1.5 py-0.5 font-semibold border',
                      rl.status === 'ACTIVE'
                        ? 'bg-emerald-900/40 text-emerald-300 border-emerald-800/60 hover:bg-emerald-900/60'
                        : rl.status === 'PAUSED'
                        ? 'bg-amber-900/40 text-amber-300 border-amber-800/60 hover:bg-amber-900/60'
                        : 'bg-surface text-fg-secondary border-line hover:bg-raised',
                    )}
                    title={`Linked to ${rl.run_number} (${runLinkStatusLabel(rl.status)})`}>
                    <Package size={9} /> {rl.run_number}
                  </button>
                ))
              )}
            </div>

            <EmptiesOutSummary delivery={d} loans={loans} readFailed={loansReadFailed} />

          </div>
          <div className="flex flex-col items-end gap-1.5 shrink-0">
            <StatusPill object="delivery" status={d.status} />
            {(() => {
              // Admin / delivery check vs. the latest delivery addition's added_at:
              //   - never checked  → no badge
              //   - checked AFTER  latest load → green "Delivery checked"
              //   - checked BEFORE latest load → amber "New load to check"
              const checkedAt = (d as Delivery & { admin_checked_at?: string | null }).admin_checked_at
              if (!checkedAt) return null
              const latestLoadAt = orderedLoads.length > 0
                ? orderedLoads[orderedLoads.length - 1].added_at
                : null
              const stale = latestLoadAt != null && latestLoadAt > checkedAt
              return (
                <span className={clsx(
                  'text-caption uppercase tracking-wider rounded-full px-2 py-0.5 font-semibold flex items-center gap-1',
                  stale ? 'bg-warning-soft text-warning' : 'bg-success-soft text-success',
                )}>
                  <ShieldCheck size={10} /> {stale ? 'New load to check' : 'Delivery checked'}
                </span>
              )
            })()}
            {(() => {
              // Tipping check vs. latest tipping_log.tipped_at — same logic.
              const checkedAt = (d as Delivery & { tipping_checked_at?: string | null }).tipping_checked_at
              if (!checkedAt) return null
              const stale = latestTipAt != null && latestTipAt > checkedAt
              return (
                <span className={clsx(
                  'text-caption uppercase tracking-wider rounded-full px-2 py-0.5 font-semibold flex items-center gap-1',
                  stale ? 'bg-warning-soft text-warning' : 'bg-success-soft text-success',
                )}>
                  <ShieldCheck size={10} /> {stale ? 'New tip to check' : 'Tipping checked'}
                </span>
              )
            })()}
            {(d as Delivery & { paltrack_status?: string }).paltrack_status === 'pending' && (
              <span className="text-caption uppercase tracking-wider rounded-full px-2 py-0.5 bg-warning-soft text-warning font-semibold">
                Paltrack pending
              </span>
            )}
            {isGrouped && (
              <span className="text-caption bg-blue-900/50 text-blue-300 rounded-full px-2 py-0.5 flex items-center gap-1">
                {loadCount} loads · {expanded ? '▲' : '▼'}
              </span>
            )}
          </div>
        </div>

        {/* Intake path + received timestamp. Date/time always on the card
            so the operator doesn't need to expand or open the detail to see
            when a delivery landed. For grouped (multi-load) deliveries the
            parent received_at is the first load's gate-in; per-load times
            live in the expanded breakdown below. */}
        <p className="text-caption text-fg-muted flex items-center gap-1.5 flex-wrap">
          <span>{intakePathLabel(d.intake_path)}</span>
          <span className="text-fg-muted">·</span>
          <span className="tabular-nums">
            {new Date(d.received_at).toLocaleString('en-ZA', {
              day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit',
            })}
          </span>
        </p>

        {/* Bins progress */}
        <div>
          <div className="flex justify-between text-caption tabular-nums text-fg-muted mb-1">
            <span>{d.bins_remaining} bins remaining</span>
            <span>{d.bins_received} received · {pct}% tipped</span>
          </div>
          <div className="h-2 bg-raised rounded-full overflow-hidden">
            <div className="h-full bg-green-500 rounded-full transition-[width]"
              style={{ width: `${pct}%` }} />
          </div>
          <div className="flex justify-between text-caption mt-1.5">
            <span className="text-fg-muted">Net tipped</span>
            <span className={clsx('tabular-nums font-semibold',
              kgTipped > 0 ? 'text-success' : 'text-fg-muted')}>
              {kgTipped > 0
                ? `${kgTipped.toLocaleString('en-ZA', { maximumFractionDigits: 0 })} kg`
                : '— kg'}
            </span>
          </div>
        </div>
      </button>
      {/* Outside the header button: a dialog in a button is invalid, and its taps would toggle the card. */}
      {runsSheet && <RunsForDeliverySheet delivery={d} onClose={() => setRunsSheet(false)} />}

      {/* Loads breakdown — visible when expanded */}
      {isGrouped && expanded && (
        <div className="border-t border-line bg-surface/50 px-4 py-3 space-y-1.5">
          <p className="text-caption font-semibold text-fg-muted uppercase tracking-wider mb-2">Individual Loads</p>
          {orderedLoads.map((load, i) => {
            const isFirstLoad = i === 0
            // First load's DN may be backfilled from the parent row even
            // when delivery_additions.delivery_note_number is NULL
            // (legacy rows that predate mig 208). Fall back so the value
            // shows in the breakdown without requiring a manual edit.
            const displayDn = load.delivery_note_number
              ?? (isFirstLoad ? parentDn ?? null : null)
            const isEditing = editingAdditionId === load.addition_id
            return (
              <div key={load.addition_id} className="flex items-center justify-between gap-2 text-sm py-1 border-b border-line/50 last:border-0">
                <div className="flex items-center gap-2 flex-wrap min-w-0">
                  <span className="text-fg-muted text-caption w-4">{i + 1}.</span>
                  <span className="text-fg font-semibold tabular-nums">{load.bins} bins</span>

                  {/* DN slot — always rendered so the operator can see
                      what's set / missing on every load. Admin / manager
                      can tap to edit a missing or wrong DN inline. */}
                  {isEditing ? (
                    <span className="flex items-center gap-1">
                      <input
                        autoFocus
                        type="text"
                        value={editingDn}
                        onChange={e => setEditingDn(e.target.value)}
                        onKeyDown={e => {
                          if (e.key === 'Enter') saveAdditionDn(load, isFirstLoad)
                          if (e.key === 'Escape') { setEditingAdditionId(null); setEditingDn('') }
                        }}
                        placeholder="DN#"
                        className="bg-canvas border border-line rounded-lg px-1.5 py-0.5 text-caption font-mono w-20 text-fg"
                      />
                      <button
                        onClick={() => saveAdditionDn(load, isFirstLoad)}
                        disabled={savingDn}
                        className="inline-flex items-center min-h-[44px] px-2 rounded-lg text-caption text-success hover:text-emerald-300 hover:bg-surface disabled:opacity-50">
                        {savingDn ? '…' : 'save'}
                      </button>
                      <button
                        onClick={() => { setEditingAdditionId(null); setEditingDn('') }}
                        className="inline-flex items-center min-h-[44px] px-2 rounded-lg text-caption text-fg-muted hover:text-fg-secondary hover:bg-surface">
                        cancel
                      </button>
                    </span>
                  ) : displayDn ? (
                    <button
                      onClick={() => {
                        if (!canEditDn) return
                        setEditingAdditionId(load.addition_id)
                        setEditingDn(displayDn ?? '')
                      }}
                      disabled={!canEditDn}
                      title={canEditDn ? 'Tap to edit' : undefined}
                      className={clsx(
                        'text-caption font-mono text-fg-secondary inline-flex items-center gap-1',
                        canEditDn && 'hover:text-fg hover:underline',
                      )}>
                      DN {displayDn}
                    </button>
                  ) : canEditDn ? (
                    <button
                      onClick={() => {
                        setEditingAdditionId(load.addition_id)
                        setEditingDn('')
                      }}
                      className="text-caption italic text-amber-400 hover:text-amber-300 underline">
                      + add DN
                    </button>
                  ) : (
                    <span className="text-caption italic text-fg-muted">no DN</span>
                  )}

                  <span className={clsx('text-caption rounded-full px-1.5 py-0.5',
                    load.source === 'WHATSAPP'
                      ? 'bg-green-900/50 text-green-400'
                      : 'bg-raised text-fg-secondary')}>
                    {load.source === 'WHATSAPP' ? '📱 WA' : '🖥 Manual'}
                  </span>
                </div>
                <span className="text-caption text-fg-muted shrink-0">
                  {new Date(load.added_at).toLocaleTimeString('en-ZA', { hour: '2-digit', minute: '2-digit' })}
                </span>
              </div>
            )
          })}
        </div>
      )}

      {/* Action buttons + detail link */}
      <div className="px-4 pb-4 flex items-center gap-2">
        <div className="flex-1 min-w-0">
          <DeliveryActionButtons delivery={d} onTransition={onTransition} busy={transitionBusy} saving={transitionSaving} error={transitionError} done={transitionDone} />
        </div>
        <Link
          to={`/deliveries/${d.delivery_id}`}
          state={paltrackWindowState(windowFromIso)}
          className="btn-ghost btn-sm flex items-center gap-1 shrink-0"
          title="View delivery details">
          View <ChevronRight size={12} />
        </Link>
      </div>
    </div>
  )
}

// ---------------------------------------------------------------------------
// Action buttons based on current status + intake path
// ---------------------------------------------------------------------------
// ---------------------------------------------------------------------------
// EmptiesOutSummary — per-load empties out, listed on the card. Mig 342
// links bin_loans to the specific delivery_additions row they were issued
// alongside. Grouped deliveries that had empties on multiple loads show
// every event here (e.g. "Load 1: 20 × KF49 · DN H48" + "Load 2: …").
// Falls back to the legacy parent snapshot for pre-mig-342 deliveries.
//
// `loans` is fed from the parent's batched loansByDelivery query (one .in over
// all visible deliveries) — NOT a per-card query, which was an N+1 storm.
// ---------------------------------------------------------------------------
type EmptiesLoan = {
  loan_id: string
  delivery_addition_id: string | null
  bin_count: number
  container_type: ContainerType | null
  notes: string | null
  issued_at: string
}

function EmptiesOutSummary({ delivery: d, loans, readFailed = false }: { delivery: Delivery; loans: EmptiesLoan[]; readFailed?: boolean }) {
  // Index additions by id for "Load N" labelling using the same order
  // the breakdown uses (insertion order).
  const orderedLoads = [...(d.delivery_additions ?? [])]
    .sort((a, b) => a.added_at.localeCompare(b.added_at))
  const loadIdxById = new Map(orderedLoads.map((a, i) => [a.addition_id, i + 1]))

  // Legacy fallback: parent snapshot for pre-mig-342 deliveries whose
  // bin_loans aren't linked to additions yet (we backfilled the first
  // loan only — anything older was a single-load delivery anyway).
  const dx = d as Delivery & {
    empties_issued_count?:        number | null
    empties_container_type?:      ContainerType | null
    empties_delivery_note_number?: string | null
  }

  if (loans.length === 0 && readFailed) {
    return (
      <p className="text-caption mt-1 flex items-center gap-1 text-warning">
        <Package size={10} />
        <span>Empties out: not read</span>
      </p>
    )
  }
  if (loans.length === 0) {
    if (!dx.empties_issued_count) return null
    const typeLabel = dx.empties_container_type
      ? CONTAINER_LABEL[dx.empties_container_type]
      : 'bins'
    return (
      <p className="text-caption mt-1 flex items-center gap-1 text-warning">
        <Package size={10} />
        <span>Empties out: <span className="font-semibold">{dx.empties_issued_count}</span> × {typeLabel}</span>
        {dx.empties_delivery_note_number && (
          <span className="font-mono text-warning/70">· DN {dx.empties_delivery_note_number}</span>
        )}
      </p>
    )
  }

  // Pull the DN# out of the loan's auto-generated note ("Issued at gate
  // alongside delivery — note H48"). Keeps us off another join hop.
  function extractDn(notes: string | null): string | null {
    if (!notes) return null
    const m = notes.match(/note\s+([A-Za-z0-9-]+)/)
    return m?.[1] ?? null
  }

  return (
    <div className="text-caption mt-1 space-y-0.5 text-warning">
      {loans.map(loan => {
        const typeLabel = loan.container_type
          ? CONTAINER_LABEL[loan.container_type]
          : 'bins'
        const idx = loan.delivery_addition_id
          ? loadIdxById.get(loan.delivery_addition_id) ?? null
          : null
        const dn = extractDn(loan.notes)
        return (
          <p key={loan.loan_id} className="flex items-center gap-1 flex-wrap">
            <Package size={10} />
            <span>
              Empties out{idx && loans.length > 1 ? ` (load ${idx})` : ''}:{' '}
              <span className="font-semibold">{loan.bin_count}</span> × {typeLabel}
            </span>
            {dn && <span className="font-mono text-warning/70">· DN {dn}</span>}
          </p>
        )
      })}
    </div>
  )
}

// ActionButtons moved to ./DeliveryActionButtons (renders the deliveryActions
// state machine from src/lib/deliveryStatus.ts, unit + component tested).

// One WhatsApp pre-announcement card: the grower's photo (if any), what is
// on its way, and Confirm. Shared by the open list and the >48 h fold (UF-13).
function AnnouncementCard({ d, onConfirm, onNotComing, notComingPending }: {
  d: Delivery
  onConfirm: (d: Delivery) => void
  // UF-15: given only to a viewer who may mark a truck as not coming.
  onNotComing?: (d: Delivery) => void
  notComingPending?: boolean
}) {
  return (
    <div className="bg-raised rounded-xl overflow-hidden">
      {/* Photo — shown only if grower sent one */}
      {d.scale_photo_url && (
        <a href={d.scale_photo_url} target="_blank" rel="noopener noreferrer">
          <img
            src={d.scale_photo_url}
            alt="Load photo"
            className="w-full max-h-48 object-cover"
          />
        </a>
      )}
      <div className="p-3 flex items-center justify-between gap-3">
        <div className="flex-1 min-w-0">
          <p className="font-semibold text-fg truncate">{d.grower} — {d.variety}</p>
          {d.bins_received > 0 && (
            <p className="text-sm tabular-nums text-fg-muted">{d.bins_received} bins expected</p>
          )}
          {d.block && (
            <p className="text-caption text-fg-muted mt-0.5">Block {d.block}</p>
          )}
        </div>
        <div className="flex items-center gap-2 shrink-0">
          {onNotComing && (
            <Button variant="secondary" size="sm" type="button"
              onClick={() => onNotComing(d)}
              disabled={notComingPending}
              aria-label={`Not coming: ${d.grower} — ${d.variety}`}
              className="shrink-0">
              Not coming
            </Button>
          )}
          <Button variant="primary" size="sm"
            onClick={() => onConfirm(d)}
            className="shrink-0">
            <Truck size={14} /> Confirm
          </Button>
        </div>
      </div>
    </div>
  )
}
